import axios from "axios";
import classnames from "classnames";
import React, { Component } from "react";
import cookie from "react-cookies";
import SeoHead from "../../components/SeoHead";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
  TabContent,
  TabPane,
} from "reactstrap";
import ConfirmModal from "../../components/common/ConfirmModal";
import constants from "../../Constants";
import PlacesAutocomplete from "../../components/common/PlacesAutocomplete";
import PartnerAccessPanel from "../../components/PartnerAccessPanel";
import WidgetEmbedMock, { EmbedFlowStrip } from "../../components/integrations/WidgetEmbedMock";
import PartnerApiStageMock, { PartnerApiFlowStrip } from "../../components/integrations/PartnerApiStageMock";
import { PARTNER_API_STAGES, WIDGET_EMBED_STAGES } from "../../components/integrations/embedFlowStages";
import {
  API_BASE,
  MEASUREMENTS,
  API_CATALOG,
  DOCS_PATH,
  CLAIMS_INFO,
} from "../api-docs/partnerApiContent";
import "./integrations.css";

const baseURL = constants.baseURL;

function newWarehouseId() {
  return "wh_" + Math.random().toString(36).slice(2, 10);
}

function widgetFunnelPct(part, whole) {
  if (!whole || !part) return null;
  return Math.round((part / whole) * 100);
}

function widgetEventLabel(event) {
  const labels = {
    config: "Script loads",
    open: "Opens",
    quoted: "Quotes",
    booked: "Bookings",
  };
  return labels[event] || String(event || "Unknown");
}

function emptyWarehouse(isDefault) {
  return {
    id: newWarehouseId(),
    label: "",
    address: "",
    lat: null,
    lng: null,
    is_default: !!isDefault,
  };
}

function pickSettingsForm(state) {
  return {
    warehouses: (state.warehouses || []).map((w) => ({
      id: w.id,
      label: w.label || "",
      address: w.address || "",
      lat: w.lat ?? null,
      lng: w.lng ?? null,
      is_default: !!w.is_default,
    })),
    defaultWarehouseId: state.defaultWarehouseId || null,
    defaultVehicle: state.defaultVehicle || "",
    defaultHelpers: Number(state.defaultHelpers) || 0,
    defaultFloors: Number(state.defaultFloors) || 0,
    allowedVehicles: [...(state.allowedVehicles || [])].sort(),
    aiPriceTips: state.aiPriceTips !== false,
    defaultCategory: state.defaultCategory || "",
    defaultPromocode: state.defaultPromocode || "",
    billingMode: state.billingMode || "prepaid_per_order",
    billingEmail: state.billingEmail || "",
    billingPaymentPayer: state.billingPaymentPayer || "merchant",
  };
}

function settingsFormEqual(a, b) {
  if (!a || !b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

const SECTIONS = [
  { id: "overview", label: "Overview", icon: "mdi-view-dashboard" },
  { id: "domains", label: "Domains", icon: "mdi-web" },
  { id: "config", label: "Configuration", icon: "mdi-cog" },
  { id: "install", label: "Install", icon: "mdi-code-tags" },
  { id: "usage", label: "Usage", icon: "mdi-chart-line" },
  { id: "docs", label: "Docs", icon: "mdi-book-open-variant" },
];

function partnerPlanCardState(plan, ctx) {
  const { hqProvisioned, effectiveCurrentPlan, pendingPlanId, pendingGoLive } = ctx;

  const isPendingPaid = pendingPlanId === plan.id;
  const isCurrent = !!hqProvisioned && !!effectiveCurrentPlan && plan.id === effectiveCurrentPlan;
  const isDefaultSandbox = !hqProvisioned && plan.id === "sandbox";

  let badge = null;
  if (isPendingPaid) {
    badge = { label: "Paid - pending go-live", className: "badge bg-warning text-dark" };
  } else if (isCurrent) {
    badge = { label: "Current", className: "badge bg-primary" };
  } else if (isDefaultSandbox) {
    badge = { label: "Default", className: "badge bg-secondary" };
  }

  const canSubscribe =
    plan.self_serve &&
    plan.id !== "sandbox" &&
    hqProvisioned &&
    !pendingGoLive &&
    !isCurrent &&
    !isPendingPaid;

  let footerText = null;
  if (canSubscribe) {
    return { isCurrent, isPendingPaid, isDefaultSandbox, badge, footerText, showSubscribe: true, highlight: isCurrent || isPendingPaid || isDefaultSandbox };
  }

  if (!hqProvisioned) {
    footerText =
      plan.id === "sandbox"
        ? "Free after QikTruck activates your account"
        : "Available after Partner API access is activated";
  } else if (isPendingPaid) {
    footerText = "Payment received - waiting for go-live";
  } else if (pendingGoLive && plan.self_serve && plan.id !== "sandbox" && !isPendingPaid) {
    footerText = "Complete go-live before changing plan";
  } else if (isCurrent) {
    footerText = plan.id === "sandbox" ? "Active sandbox plan" : "Current plan";
  } else if (plan.id === "sandbox") {
    footerText = "Included free with activated access";
  } else if (plan.self_serve) {
    footerText = "Subscribe when ready for production";
  } else {
    footerText = "Included with activated access";
  }

  return {
    isCurrent,
    isPendingPaid,
    isDefaultSandbox,
    badge,
    footerText,
    showSubscribe: false,
    highlight: isCurrent || isPendingPaid || isDefaultSandbox,
  };
}

const PARTNER_SECTIONS = [
  { id: "overview", label: "Overview", icon: "mdi-view-dashboard" },
  { id: "plans", label: "Plans", icon: "mdi-package-variant" },
  { id: "apply", label: "Request access", icon: "mdi-key-variant" },
  { id: "keys", label: "API keys", icon: "mdi-key" },
  { id: "usage", label: "Usage", icon: "mdi-chart-line" },
  { id: "billing", label: "Billing", icon: "mdi-credit-card-outline" },
  { id: "claims", label: "Claims", icon: "mdi-shield-alert" },
  { id: "docs", label: "API docs", icon: "mdi-book-open-variant" },
];

const PARTNER_ACCESS_STORAGE_KEY = "qt-partner-access-submitted";

function readPartnerAccessStorage() {
  try {
    const raw = localStorage.getItem(PARTNER_ACCESS_STORAGE_KEY);
    if (!raw) return { submitted: false, email: "" };
    const parsed = JSON.parse(raw);
    return { submitted: !!parsed?.submitted, email: parsed?.email || "" };
  } catch {
    return { submitted: false, email: "" };
  }
}

const INTEGRATION_TYPES = [
  {
    id: "widget",
    label: "Website Widget",
    icon: "mdi-puzzle",
    status: "available",
    desc: "Live delivery quotes on any product page. One script tag, hosted checkout.",
    eta: "~5 min setup",
  },
  {
    id: "partner",
    label: "Partner API",
    icon: "mdi-flash",
    status: "available",
    desc: "Connect a custom storefront, ERP, warehouse, or app to quote, book, and track deliveries.",
    eta: "Request access",
  },
  {
    id: "woocommerce",
    label: "WooCommerce",
    icon: "mdi-cart",
    status: "available",
    desc: "Checkout delivery pricing synced with WooCommerce orders.",
    eta: "Download plugin",
  },
  {
    id: "shopify",
    label: "Shopify",
    icon: "mdi-shopping",
    status: "available",
    desc: "Checkout delivery pricing and automatic trip booking for Shopify stores.",
    eta: "Install app",
  },
  {
    id: "magento",
    label: "Magento",
    icon: "mdi-store",
    status: "soon",
    desc: "Extension for Magento storefronts and fulfilment.",
    eta: "Coming soon",
  },
];

const PLATFORM_INSTALLS = [
  {
    id: "shopify",
    icon: "mdi-shopping",
    name: "Shopify",
    steps: "Install the QikTruck app from the Shopify App Store (or use the install link), then open Settings in the app to connect your Partner API key.",
  },
  {
    id: "wordpress",
    icon: "mdi-wordpress",
    name: "WordPress / WooCommerce",
    steps: "Appearance → Theme File Editor → footer.php, or use a \"Custom HTML\" footer widget plugin.",
  },
  {
    id: "html",
    icon: "mdi-language-html5",
    name: "Custom HTML",
    steps: "Paste the snippet before </body> on product pages, or via Google Tag Manager.",
  },
];

function readIntegrationFromUrl() {
  try {
    if (typeof window !== "undefined") {
      if (window.location.pathname === "/widget") return "widget";
      if (window.location.pathname === "/developers") return "partner";
      if (window.location.pathname === "/partner-plans") return "partner";
    }
    const q = new URLSearchParams(window.location.search);
    let t = q.get("type") || "widget";
    if (t === "enterprise") t = "partner";
    if (t === "api") t = "widget";
    return INTEGRATION_TYPES.some((x) => x.id === t) ? t : "widget";
  } catch {
    return "widget";
  }
}

function readPartnerSectionFromUrl() {
  try {
    if (typeof window !== "undefined" && window.location.pathname === "/partner-plans") {
      return "plans";
    }
    const q = new URLSearchParams(window.location.search);
    const section = q.get("section");
    if (q.get("type") === "partner" && section && PARTNER_SECTIONS.some((s) => s.id === section)) {
      return section;
    }
  } catch {
    /* noop */
  }
  return null;
}

const WIDGET_VEHICLE_OPTIONS = [
  { id: "minivanopen", label: "Mini van", short: "Mini" },
  { id: "1tonopen", label: "1 ton bakkie", short: "1 ton" },
  { id: "1.5tonopen", label: "1.5 ton", short: "1.5 ton" },
  { id: "4tonopen", label: "4 ton truck", short: "4 ton" },
  { id: "8tonopen", label: "8 ton truck", short: "8 ton" },
];

function truckImageUrl(truckType) {
  if (!truckType) return "";
  return `https://media.qiktruck.co.za/3d-${truckType}.png`;
}

const CONFIG_OPTIONS = [
  ["data-qt-site", "Your public site id (qt_pub_...). Required.", "-"],
  ["data-qt-mode", "bubble (floating), inline (in-page), or button", "bubble"],
  ["data-qt-label", "Launcher button text", "Get a delivery price"],
  ["data-qt-position", "Bubble corner: br, bl, tr, tl", "br"],
  ["data-qt-size", "Launcher size: sm, md, lg", "md"],
  ["data-qt-color", "Accent / launcher background colour", "#72574d"],
  ["data-qt-text-color", "Launcher text colour", "#ffffff"],
  ["data-qt-radius", "Launcher corner radius", "999px"],
  ["data-qt-target", "CSS selector to mount into (inline mode)", "[data-qt-widget]"],
  ["data-qt-inline-height", "Inline iframe height in px", "640"],
  ["data-qt-description", "Prefill item description (shown in header + used for pricing)", "-"],
  ["data-qt-title", "Custom headline on the hosted quote page", "-"],
  ["data-qt-subtitle", "Custom subtext under the headline", "-"],
  ["data-qt-category", "Prefill load category", "furniture"],
  ["data-qt-promocode", "Apply a QikTruck promo code", "-"],
  ["data-qt-trucktype", "Prefill vehicle type (e.g. 1tonopen)", "-"],
  ["data-qt-email", "Shopper email from checkout", "-"],
  ["data-qt-mobile", "Shopper mobile from checkout", "-"],
  ["data-qt-firstname", "Shopper first name", "-"],
  ["data-qt-lastname", "Shopper surname", "-"],
  ["data-qt-autoquote", "1 = price on open when address + description passed", "auto when prefill complete"],
];

const DNS_GUIDES = [
  {
    id: "cloudflare",
    name: "Cloudflare",
    icon: "mdi-cloud",
    steps: [
      "Log in to Cloudflare → select your domain.",
      "Go to DNS → Records → Add record.",
      "Type: TXT · Name: paste the Host below (e.g. _qiktruck) · Content: paste the Value.",
      "TTL: Auto · Save. Wait 2-10 minutes, then click Verify DNS.",
    ],
  },
  {
    id: "godaddy",
    name: "GoDaddy",
    icon: "mdi-web",
    steps: [
      "GoDaddy → My Products → your domain → DNS.",
      "Click Add → Type: TXT.",
      "Host: paste the Host field below · TXT Value: paste the Value.",
      "Save. Propagation can take up to 30 minutes.",
    ],
  },
  {
    id: "cpanel",
    name: "cPanel / hosting panel",
    icon: "mdi-server",
    steps: [
      "Open cPanel → Zone Editor (or Advanced DNS).",
      "Add Record → Type TXT.",
      "Name: Host below · Record: Value below · TTL 14400.",
      "Save the zone file, then Verify here.",
    ],
  },
  {
    id: "afrihost",
    name: "Afrihost / Xneelo / 1-grid",
    icon: "mdi-earth",
    steps: [
      "Client zone → DNS management for your domain.",
      "Add a TXT record.",
      "Hostname: use the Host below (often _qiktruck.yourdomain).",
      "Value: paste exactly as shown. Save and wait a few minutes.",
    ],
  },
];

class Integrations extends Component {
  constructor(props) {
    super(props);
    const partnerAccess = readPartnerAccessStorage();
    const initialIntegration = readIntegrationFromUrl();
    const urlSection = readPartnerSectionFromUrl();
    this.state = {
      activeIntegration: initialIntegration,
      activeSection:
        urlSection ||
        (initialIntegration === "partner" && !partnerAccess.submitted ? "apply" : "overview"),
      loading: true,
      error: "",
      notice: "",
      siteId: null,
      domains: [],
      newDomain: "",
      adding: false,
      verifying: "",
      togglingStatus: "",
      removing: "",
      copied: "",
      installTab: "html",
      // config
      warehouses: [emptyWarehouse(true)],
      defaultWarehouseId: null,
      defaultVehicle: "",
      defaultHelpers: 0,
      defaultFloors: 0,
      allowedVehicles: [],
      aiPriceTips: true,
      defaultCategory: "",
      defaultPromocode: "",
      billingMode: "prepaid_per_order",
      billingEmail: "",
      billingPaymentPayer: "merchant",
      settingsSaving: false,
      settingsNotice: "",
      settingsBaseline: null,
      domainRequests: null,
      // usage
      usage: null,
      usageLoading: false,
      partnerAccessSubmitted: false,
      partnerAccessEmail: "",
      partnerKeys: [],
      partnerAccess: null,
      partnerKeysLoading: false,
      partnerKeyCreating: false,
      partnerKeyRevoking: "",
      partnerNewKeyLabel: "",
      partnerNewKeyPrefix: "qt_test",
      partnerIssuedKey: null,
      partnerPortal: null,
      partnerPortalLoading: false,
      partnerPlansCatalog: null,
      partnerPlanBilling: null,
      partnerPlansLoading: false,
      partnerPlansError: "",
      planCheckoutLoading: "",
      confirmModal: null,
    };
  }

  componentDidMount() {
    document.title = "Integrations | QikTruck";
    if (!cookie.load("session-id-qt")) {
      window.location.href = "/login";
      return;
    }
    axios
      .get(baseURL + "/merchant/team/context", {
        headers: {
          "Content-Type": "application/json",
          Authorization: cookie.load("session-id-qt"),
        },
      })
      .then((res) => {
        if (res.data?.canManageIntegrations === false) {
          window.location.href = "/dashboard";
        }
      })
      .catch(() => {});
    const partnerAccess = readPartnerAccessStorage();
    const patch = {
      partnerAccessSubmitted: partnerAccess.submitted,
      partnerAccessEmail: partnerAccess.email,
    };
    if (
      this.state.activeIntegration === "partner" &&
      !partnerAccess.submitted &&
      !readPartnerSectionFromUrl()
    ) {
      patch.activeSection = "apply";
    }
    this.setState(patch);
    this.fetchDomains();
    this.fetchSettings();
    this.fetchUsage();
    this.fetchPartnerKeys();
    if (window.location.pathname === "/partner-plans" || readPartnerSectionFromUrl() === "plans") {
      this.fetchPartnerPlans();
    }
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("subscription") === "success") {
        this.setState({
          notice:
            "Payment received. QikTruck will review and enable production access - keep testing with sandbox keys until we email you.",
        });
      }
    } catch {
      /* noop */
    }
  }

  pickIntegration = (id) => {
    const item = INTEGRATION_TYPES.find((x) => x.id === id);
    if (!item || item.status === "soon") return;
    this.setState({ activeIntegration: id, activeSection: "overview" });
    try {
      const url = new URL(window.location.href);
      if (id === "widget") url.searchParams.delete("type");
      else url.searchParams.set("type", id);
      url.searchParams.delete("section");
      if (url.pathname === "/developers" || url.pathname === "/partner-plans") {
        url.pathname = "/integrations";
      }
      window.history.replaceState({}, "", url.pathname + url.search);
    } catch {
      /* noop */
    }
  };

  auth() {
    return {
      headers: { "Content-Type": "application/json", Authorization: cookie.load("session-id-qt") },
    };
  }

  go = (id) => {
    this.setState({ activeSection: id });
    if (id === "usage" && !this.state.usage) this.fetchUsage();
    if (id === "keys") this.fetchPartnerKeys();
    if (id === "usage" || id === "billing" || id === "claims") this.fetchPartnerPortal();
    if (id === "plans") this.fetchPartnerPlans();
    try {
      const url = new URL(window.location.href);
      if (this.state.activeIntegration === "partner") {
        if (id === "plans") {
          url.pathname = "/partner-plans";
          url.search = "";
        } else {
          url.pathname = url.pathname === "/partner-plans" ? "/integrations" : url.pathname;
          url.searchParams.set("type", "partner");
          url.searchParams.set("section", id);
        }
        window.history.replaceState({}, "", url.pathname + url.search);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { /* noop */ }
  };

  fetchPartnerKeys = () => {
    if (!cookie.load("session-id-qt")) return;
    this.setState({ partnerKeysLoading: true });
    axios
      .get(baseURL + "/partner/keys", this.auth())
      .then((res) => {
        const access = res.data?.access || null;
        const appStatus = access?.application_status;
        const hasApplication = appStatus && appStatus.state !== "none";
        this.setState({
          partnerKeysLoading: false,
          partnerKeys: res.data?.keys || [],
          partnerAccess: access,
          ...(hasApplication
            ? {
                partnerAccessSubmitted: true,
                partnerAccessEmail:
                  appStatus.latest_application?.email || this.state.partnerAccessEmail,
              }
            : {}),
        });
      })
      .catch(() => {
        this.setState({ partnerKeysLoading: false });
      });
  };

  fetchPartnerPortal = () => {
    if (!cookie.load("session-id-qt")) return;
    this.setState({ partnerPortalLoading: true });
    axios
      .get(baseURL + "/partner/portal/summary", this.auth())
      .then((res) => {
        this.setState({
          partnerPortalLoading: false,
          partnerPortal: res.data || null,
          partnerAccess: res.data?.access || this.state.partnerAccess,
          partnerPlanBilling: res.data?.plan_billing || this.state.partnerPlanBilling,
        });
      })
      .catch(() => {
        this.setState({ partnerPortalLoading: false });
      });
  };

  fetchPartnerPlans = () => {
    this.setState({ partnerPlansLoading: true, partnerPlansError: "" });
    const catalogReq = axios.get(baseURL + "/partner/plans");
    const billingReq = cookie.load("session-id-qt")
      ? axios.get(baseURL + "/partner/plans/billing", this.auth())
      : Promise.resolve({ data: null });
    const accessReq = cookie.load("session-id-qt")
      ? axios.get(baseURL + "/partner/keys", this.auth())
      : Promise.resolve({ data: null });
    Promise.allSettled([catalogReq, billingReq, accessReq])
      .then(([catalogResult, billingResult, keysResult]) => {
        const catalog = catalogResult.status === "fulfilled" ? catalogResult.value : null;
        const billing = billingResult.status === "fulfilled" ? billingResult.value : null;
        const keysRes = keysResult.status === "fulfilled" ? keysResult.value : null;
        const plans = catalog?.data?.plans || [];
        const catalogFailed = catalogResult.status === "rejected" || plans.length === 0;
        this.setState({
          partnerPlansLoading: false,
          partnerPlansCatalog: plans,
          partnerPlanBilling: billing?.data || null,
          partnerAccess: keysRes?.data?.access || this.state.partnerAccess,
          partnerPlansError: catalogFailed
            ? "Could not load plan catalog. Check your connection and try again."
            : "",
        });
      })
      .catch(() => {
        this.setState({
          partnerPlansLoading: false,
          partnerPlansError: "Could not load plans.",
        });
      });
  };

  subscribeToPlan = (planId) => {
    if (!cookie.load("session-id-qt")) {
      window.location.href = "/login?redirect=" + encodeURIComponent("/partner-plans");
      return;
    }
    this.setState({ planCheckoutLoading: planId });
    axios
      .post(baseURL + "/partner/plans/" + planId + "/subscribe", {}, this.auth())
      .then((res) => {
        const url = res.data?.payment_page_url;
        if (url) window.location.href = url;
        else this.setState({ planCheckoutLoading: "" });
      })
      .catch((err) => {
        this.setState({ planCheckoutLoading: "", notice: err.response?.data?.message || "Could not start checkout." });
      });
  };

  purchaseAcceptPack = (packId) => {
    if (!cookie.load("session-id-qt")) {
      window.location.href = "/login?redirect=" + encodeURIComponent("/partner-plans");
      return;
    }
    this.setState({ planCheckoutLoading: packId });
    axios
      .post(baseURL + "/partner/plans/accept-packs/" + packId + "/purchase", {}, this.auth())
      .then((res) => {
        const url = res.data?.payment_page_url;
        if (url) window.location.href = url;
        else this.setState({ planCheckoutLoading: "" });
      })
      .catch((err) => {
        this.setState({ planCheckoutLoading: "", notice: err.response?.data?.message || "Could not start checkout." });
      });
  };

  settlePlatformFees = () => {
    this.setState({ planCheckoutLoading: "fees" });
    axios
      .post(baseURL + "/partner/plans/settle-platform-fees", {}, this.auth())
      .then((res) => {
        const url = res.data?.payment_page_url;
        if (url) window.location.href = url;
        else this.setState({ planCheckoutLoading: "" });
      })
      .catch((err) => {
        this.setState({ planCheckoutLoading: "", notice: err.response?.data?.message || "Could not start payment." });
      });
  };

  formatCents(cents) {
    const n = Number(cents) || 0;
    return "R" + (n / 100).toFixed(2);
  }

  renderPartnerPortalGate(message) {
    return (
      <div className="qt-card">
        <div className="qt-card-body">
          <Alert color="info" className="mb-0">{message}</Alert>
        </div>
      </div>
    );
  }

  renderPartnerPlans() {
    const s = this.state;
    if (s.partnerPlansLoading && !s.partnerPlansCatalog) {
      return (
        <div className="qt-card"><div className="qt-card-body text-center py-5"><Spinner color="primary" /></div></div>
      );
    }
    const plans = s.partnerPlansCatalog || [];
    const billing = s.partnerPlanBilling || {};
    const access = s.partnerAccess || s.partnerPortal?.access || {};
    const hqProvisioned = billing.hq_provisioned ?? !!access.hq_provisioned;
    const liveEnabled = !!access.partner_api_live_enabled;
    const pendingGoLive = billing.pending_go_live;
    const pendingPlanId = pendingGoLive?.plan_id;
    const currentPlan =
      billing.current_plan != null
        ? billing.current_plan
        : hqProvisioned
          ? s.partnerPortal?.entitlements?.plan || "sandbox"
          : null;
    const effectiveCurrentPlan = !hqProvisioned
      ? null
      : pendingGoLive
        ? "sandbox"
        : currentPlan || "sandbox";
    const planCtx = { hqProvisioned, effectiveCurrentPlan, pendingPlanId, pendingGoLive, liveEnabled };
    const subscription = billing.subscription;
    const unsettledFees = billing.unsettled_platform_fees_cents || 0;
    const acceptPacks = billing.accept_packs || [];

    return (
      <>
        <div className="qt-card mb-4">
          <div className="qt-card-body">
            {this.sectionHead("mdi-package-variant", "purple", "API plans", "Platform access separate from trip freight")}
            <p className="text-muted small mb-0">
              Sandbox testing is free after QikTruck activates your account. Subscribe to a live plan when you are ready for production -
              QikTruck will enable go-live and your paid entitlements after review.
            </p>
            {!hqProvisioned ? (
              <div className="qt-tip qt-tip--warn mt-3 mb-0">
                <i className="mdi mdi-lock-outline" />
                <div>
                  <strong>Compare plans below.</strong> PayFast checkout unlocks after QikTruck provisions sandbox keys.
                  <a href="/integrations?type=partner&section=apply" className="ms-1">Apply for access →</a>
                </div>
              </div>
            ) : null}
            {pendingGoLive ? (
              <div className="qt-tip qt-tip--info mt-3 mb-0">
                <i className="mdi mdi-clock-outline" />
                <div>
                  Payment received for <strong className="text-capitalize">{pendingPlanId}</strong>.
                  QikTruck is enabling production access - you can keep using sandbox keys until we email you.
                </div>
              </div>
            ) : null}
            {subscription && subscription.status === "active" ? (
              <div className="qt-tip qt-tip--info mt-3 mb-0">
                <i className="mdi mdi-information-outline" />
                <div>
                  Active subscription: <strong className="text-capitalize">{subscription.plan_id}</strong>
                  {subscription.grace_until ? (
                    <> · grace until {new Date(subscription.grace_until).toLocaleDateString()}</>
                  ) : null}
                </div>
              </div>
            ) : null}
            {unsettledFees > 0 ? (
              <div className="qt-tip qt-tip--warn mt-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <i className="mdi mdi-alert-outline" />
                  Outstanding platform fees: <strong>{this.formatCents(unsettledFees)}</strong>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-warning"
                  disabled={s.planCheckoutLoading === "fees" || !hqProvisioned}
                  onClick={this.settlePlatformFees}
                >
                  Pay via PayFast
                </button>
              </div>
            ) : null}
          </div>
        </div>
        {s.partnerPlansError ? (
          <Alert color="warning" className="mb-4">
            <i className="mdi mdi-alert-outline me-1" />
            {s.partnerPlansError}{" "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={this.fetchPartnerPlans}>
              Retry
            </button>
          </Alert>
        ) : null}
        {s.partnerPlansLoading && plans.length === 0 ? (
          <div className="qt-card mb-4">
            <div className="qt-card-body text-center py-4">
              <Spinner color="primary" size="sm" /> Loading plans…
            </div>
          </div>
        ) : null}
        <div className="row g-3 mb-4">
          {plans.map((plan) => {
            const card = partnerPlanCardState(plan, planCtx);
            return (
              <div className="col-md-6 col-xl-3" key={plan.id}>
                <div className={"qt-card h-100" + (card.highlight ? " border-primary" : "")}>
                  <div className="qt-card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h3 className="h5 mb-0">{plan.headline}</h3>
                      {card.badge ? (
                        <span className={card.badge.className}>{card.badge.label}</span>
                      ) : null}
                    </div>
                    <div className="h4 text-primary mb-2">{plan.price_label}</div>
                    <p className="small text-muted">{plan.tagline}</p>
                    <ul className="small flex-grow-1 ps-3 mb-3">
                      {(plan.highlights || []).map((h) => (
                        <li key={h}>{h}</li>
                      ))}
                    </ul>
                    {card.showSubscribe ? (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm w-100"
                        disabled={!!s.planCheckoutLoading}
                        onClick={() => this.subscribeToPlan(plan.id)}
                      >
                        {s.planCheckoutLoading === plan.id ? "Redirecting…" : "Subscribe via PayFast"}
                      </button>
                    ) : plan.id === "enterprise" && !plan.self_serve ? (
                      <a href="mailto:support@qiktruck.co.za?subject=Partner%20API%20Enterprise" className="btn btn-outline-primary btn-sm w-100">
                        Contact sales
                      </a>
                    ) : (
                      <span className="small text-muted">{card.footerText}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {acceptPacks.length > 0 && hqProvisioned && liveEnabled ? (
          <div className="qt-card">
            <div className="qt-card-body">
              {this.sectionHead("mdi-plus-circle-outline", "teal", "Accept packs", "One-off PayFast top-ups for this month")}
              <div className="row g-3">
                {acceptPacks.map((pack) => (
                  <div className="col-md-6" key={pack.id}>
                    <div className="border rounded p-3 h-100 d-flex flex-column">
                      <strong>{pack.label}</strong>
                      <span className="text-muted small">{pack.description}</span>
                      <div className="h5 mt-2 mb-3">{this.formatCents(pack.price_cents)}</div>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm mt-auto align-self-start"
                        disabled={!!s.planCheckoutLoading}
                        onClick={() => this.purchaseAcceptPack(pack.id)}
                      >
                        {s.planCheckoutLoading === pack.id ? "Redirecting…" : "Buy via PayFast"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {(billing.accept_pack_bonus || 0) > 0 ? (
                <p className="small text-muted mt-3 mb-0">
                  Bonus accepts this month: <strong>+{billing.accept_pack_bonus}</strong>
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </>
    );
  }

  renderPartnerUsage() {
    const s = this.state;
    const portal = s.partnerPortal;
    if (s.partnerPortalLoading && !portal) {
      return (
        <div className="qt-card"><div className="qt-card-body text-center py-5"><Spinner color="primary" /></div></div>
      );
    }
    if (!portal?.access?.hq_provisioned) {
      return this.renderPartnerPortalGate(
        "API usage appears here after QikTruck activates your Partner API access."
      );
    }
    const usage = portal.usage || {};
    const monthly = portal.monthly_usage || {};
    const entitlements = portal.entitlements || {};
    const unpaid = portal.unpaid_orders || [];
    const outstanding = portal.outstanding || {};
    return (
      <>
        {outstanding.total_cents > 0 ? (
          <div className="qt-card mb-4 border-warning">
            <div className="qt-card-body py-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <div className="text-muted small">Outstanding balance (finished trips)</div>
                  <div className="h4 mb-0 text-warning">{this.formatCents(outstanding.total_cents)}</div>
                  <div className="small text-muted">{outstanding.order_count || 0} unpaid trip(s) on your account</div>
                </div>
                <a href="/billing" className="btn btn-primary btn-sm">
                  <i className="mdi mdi-credit-card-outline me-1" />Pay via Billing
                </a>
              </div>
            </div>
          </div>
        ) : null}
        <div className="qt-card mb-4">
          <div className="qt-card-body">
            {this.sectionHead("mdi-chart-line", "teal", "API usage", `Last ${usage.days || 30} days`)}
            <p className="text-muted small">
              Metered events: quotes, accepts, track calls, and other Partner API actions.
              {portal.plan_billing?.current_plan || (portal.access?.hq_provisioned && entitlements.plan) ? (
                <> Plan:{" "}
                  <strong className="text-capitalize">
                    {portal.plan_billing?.current_plan || entitlements.plan}
                  </strong>.
                </>
              ) : !portal.access?.hq_provisioned ? (
                <> Plan: <strong>Sandbox</strong> (default after activation).</>
              ) : null}
            </p>
            {(monthly.quotes || monthly.accepts) ? (
              <div className="row g-3 mb-3">
                <div className="col-sm-6">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">Quotes this month</div>
                    <div className="h5 mb-0">
                      {monthly.quotes?.used ?? 0}
                      {monthly.quotes?.limit != null ? ` / ${monthly.quotes.limit}` : " / unlimited"}
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">Trip accepts this month</div>
                    <div className="h5 mb-0">
                      {monthly.accepts?.used ?? 0}
                      {monthly.accepts?.limit != null ? ` / ${monthly.accepts.limit}` : " / unlimited"}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {monthly.sandbox_caps ? (
              <p className="small text-muted">
                Sandbox keys are capped at {monthly.sandbox_caps.quotes} quotes and{" "}
                {monthly.sandbox_caps.accepts} accepts per month.
              </p>
            ) : null}
            <Table size="sm" bordered responsive className="small mb-0">
              <thead className="table-light">
                <tr><th>Event</th><th>Count</th></tr>
              </thead>
              <tbody>
                {(usage.by_event || []).length === 0 ? (
                  <tr><td colSpan={2} className="text-muted text-center">No events yet</td></tr>
                ) : (
                  usage.by_event.map((row) => (
                    <tr key={row.event}>
                      <td><code>{row.event}</code></td>
                      <td>{row.count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            <p className="small text-muted mt-2 mb-0">Total events: <strong>{usage.total || 0}</strong></p>
          </div>
        </div>
        <div className="qt-card">
          <div className="qt-card-body">
            {this.sectionHead("mdi-cash-multiple", "orange", "Unpaid Partner API trips", "Finished trips awaiting payment")}
            <Table size="sm" bordered responsive className="small mb-0">
              <thead className="table-light">
                <tr><th>Order</th><th>Status</th><th>Amount</th><th>Payment</th></tr>
              </thead>
              <tbody>
                {unpaid.length === 0 ? (
                  <tr><td colSpan={4} className="text-muted text-center">No outstanding partner orders</td></tr>
                ) : (
                  unpaid.map((o) => (
                    <tr key={o.orderid}>
                      <td><code className="small">{String(o.orderid).slice(0, 8)}…</code></td>
                      <td>{o.orderstatus}</td>
                      <td>{this.formatCents(o.sellingprice)}</td>
                      <td>{o.paymentstatus || "Pending"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            {unpaid.length > 0 ? (
              <a href="/billing" className="btn btn-primary btn-sm mt-3">
                <i className="mdi mdi-credit-card-outline me-1" />Go to Billing
              </a>
            ) : null}
          </div>
        </div>
      </>
    );
  }

  renderPartnerBilling() {
    const portal = this.state.partnerPortal;
    if (this.state.partnerPortalLoading && !portal) {
      return (
        <div className="qt-card"><div className="qt-card-body text-center py-5"><Spinner color="primary" /></div></div>
      );
    }
    if (!portal?.access?.hq_provisioned) {
      return this.renderPartnerPortalGate(
        "Billing settings for Partner API unlock after your account is activated."
      );
    }
    const billing = portal.billing || {};
    const outstanding = portal.partner_outstanding || portal.outstanding || {};
    const isPostpaid = billing.mode === "postpaid_invoice";
    const defaultPayer = billing.payment_payer || "merchant";
    return (
      <div className="qt-card">
        <div className="qt-card-body">
          {outstanding.total_cents > 0 ? (
            <div className="qt-tip qt-tip--warn mb-3">
              <i className="mdi mdi-alert-outline" />
              <div>
                <strong>You owe {this.formatCents(outstanding.total_cents)}</strong>
                <p className="mb-0 small">
                  {outstanding.order_count} finished trip(s) are unpaid.
                  {billing.mode === "prepaid_per_order"
                    ? " New Partner API bookings are blocked while Partner API trips remain unpaid."
                    : " Pay via Billing or wait for your invoice cycle."}
                </p>
              </div>
            </div>
          ) : null}
          {this.sectionHead(
            "mdi-credit-card-outline",
            "mint",
            "Partner API billing",
            "Who pays QikTruck and when-separate from your end-customer checkout."
          )}
          <div className={"qt-tip mb-3 " + (isPostpaid ? "qt-tip--info" : "")}>
            <i className="mdi mdi-information-outline" />
            <div>
              <strong>Current mode: {billing.label || "Prepaid per order"}</strong>
              <p className="mb-0 small">
                {isPostpaid
                  ? "Trips accumulate on your account; QikTruck invoices your finance contact on schedule."
                  : "Merchant-paid trips are billed to your QikTruck business account after booking."}
              </p>
            </div>
          </div>
          <div className="qt-tip qt-tip--info mb-3">
            <i className="mdi mdi-cash-multiple" />
            <div>
              <strong>Default payer: {billing.payment_payer_label || (defaultPayer === "end_customer" ? "End customer via PayFast" : "Merchant account")}</strong>
              <p className="mb-0 small">
                Override per booking with <code>payment_payer</code> on accept. End-customer trips return a{" "}
                <code>payment.payment_page_url</code> on qiktruck.co.za.
              </p>
            </div>
          </div>
          <ul className="small text-muted">
            <li><strong>Merchant pays:</strong> Settle via Billing (prepaid) or invoice cycle (postpaid).</li>
            <li><strong>End customer pays:</strong> Redirect shoppers to the PayFast link from the accept response.</li>
            <li><strong>Default payer:</strong> Configuration → Billing (widget settings, shared with Partner API).</li>
          </ul>
          <a href="/billing" className="btn btn-primary me-2">
            <i className="mdi mdi-open-in-new me-1" />Open Billing
          </a>
          <a href="/api-docs?product=partner#billing" className="btn btn-outline-primary">
            Billing documentation
          </a>
        </div>
      </div>
    );
  }

  renderPartnerClaims() {
    const portal = this.state.partnerPortal;
    const formUrl = portal?.claims?.form_url || CLAIMS_INFO.form_url;
    return (
      <div className="qt-card">
        <div className="qt-card-body">
          {this.sectionHead(
            "mdi-shield-alert",
            "orange",
            "Damage & dispute claims",
            "Same Google Form and review pipeline QikTruck HQ uses."
          )}
          <p className="text-muted">
            {portal?.claims?.note || CLAIMS_INFO.note}
          </p>
          <a
            href={formUrl}
            className="btn btn-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="mdi mdi-file-document-edit-outline me-1" />
            Submit a claim
          </a>
          <p className="small text-muted mt-3 mb-0">
            Include trip/order ID, contact details, and before/after photos where possible.
          </p>
        </div>
      </div>
    );
  }

  createPartnerKey = () => {
    this.setState({ partnerKeyCreating: true });
    axios
      .post(
        baseURL + "/partner/keys",
        {
          label: this.state.partnerNewKeyLabel || "Merchant key",
          key_prefix: this.state.partnerNewKeyPrefix,
        },
        this.auth()
      )
      .then((res) => {
        this.setState({
          partnerKeyCreating: false,
          partnerIssuedKey: res.data?.api_key || null,
          partnerNewKeyLabel: "",
          partnerNewKeyPrefix: "qt_test",
          partnerAccess: res.data?.access || this.state.partnerAccess,
          notice: "API key created. Copy it now - we cannot show it again.",
        });
        this.fetchPartnerKeys();
      })
      .catch((err) => {
        this.setState({
          partnerKeyCreating: false,
          error: err.response?.data?.message || "Could not create API key.",
        });
      });
  };

  closeConfirmModal = () => {
    if (this.state.partnerKeyRevoking || this.state.removing) return;
    this.setState({ confirmModal: null });
  };

  askRevokePartnerKey = (keyId) => {
    this.setState({
      confirmModal: {
        type: "revokePartnerKey",
        keyId,
        title: "Revoke API key",
        message:
          "Revoke this API key? Integrations using it will stop working immediately.",
        confirmLabel: "Revoke key",
        confirmColor: "danger",
      },
    });
  };

  askRemoveDomain = (domain) => {
    this.setState({
      confirmModal: {
        type: "removeDomain",
        domain,
        title: "Remove domain",
        message: `Remove ${domain}? The widget will stop working on that site until you add and verify it again.`,
        confirmLabel: "Remove domain",
        confirmColor: "danger",
      },
    });
  };

  confirmModalAction = () => {
    const { confirmModal } = this.state;
    if (!confirmModal) return;
    if (confirmModal.type === "revokePartnerKey") {
      this.executeRevokePartnerKey(confirmModal.keyId);
      return;
    }
    if (confirmModal.type === "removeDomain") {
      this.executeRemoveDomain(confirmModal.domain);
    }
  };

  executeRevokePartnerKey = (keyId) => {
    this.setState({ partnerKeyRevoking: keyId });
    axios
      .post(baseURL + "/partner/keys/" + keyId + "/revoke", {}, this.auth())
      .then((res) => {
        const updated = res.data?.key;
        this.setState({
          partnerKeyRevoking: "",
          confirmModal: null,
          partnerKeys: (this.state.partnerKeys || []).map((k) =>
            k.id === keyId ? { ...k, ...updated, status: "revoked" } : k
          ),
          notice: "API key revoked.",
        });
      })
      .catch((err) => {
        this.setState({
          partnerKeyRevoking: "",
          confirmModal: null,
          error: err.response?.data?.message || "Could not revoke API key.",
        });
      });
  };

  revokePartnerKey = (keyId) => {
    this.askRevokePartnerKey(keyId);
  };

  copyPartnerKey = (text) => {
    try {
      navigator.clipboard.writeText(text);
      this.setState({ copied: "key", notice: "Copied to clipboard." });
      setTimeout(() => this.setState({ copied: "" }), 2000);
    } catch {
      /* noop */
    }
  };

  handlePartnerAccessSuccess = (email) => {
    try {
      localStorage.setItem(
        PARTNER_ACCESS_STORAGE_KEY,
        JSON.stringify({ submitted: true, email, at: new Date().toISOString() })
      );
    } catch {
      /* noop */
    }
    this.setState({
      partnerAccessSubmitted: true,
      partnerAccessEmail: email,
      activeSection: "overview",
      notice: "Partner API access request received. We'll review your application and email you when access is active.",
    });
  };

  partnerSetupProgress() {
    const submitted = this.state.partnerAccessSubmitted;
    const keysUnlocked = !!this.state.partnerAccess?.hq_provisioned;
    const hasActiveKey = (this.state.partnerKeys || []).some((k) => k.status === "active");
    const items = [
      { id: "overview", label: "Review Partner API", done: true },
      { id: "apply", label: "Submit access application", done: submitted },
      { id: "keys", label: "Partner API access activated", done: keysUnlocked },
      { id: "build", label: "Manage keys & test integration", done: keysUnlocked && hasActiveKey },
      { id: "live", label: "Go live (qt_live_ key)", done: (this.state.partnerKeys || []).some((k) => k.key_prefix === "qt_live" && k.status === "active") },
    ];
    const completed = items.filter((x) => x.done).length;
    return {
      items,
      completed,
      total: items.length,
      pct: Math.round((completed / items.length) * 100),
      submitted,
    };
  }

  nextPartnerSetupStep() {
    const prog = this.partnerSetupProgress();
    if (!prog.submitted) {
      return {
        section: "apply",
        label: "Submit access application",
        hint: "Tell us about your company - Partner API access is enabled after review.",
        icon: "mdi-key-variant",
      };
    }
    if (!this.state.partnerAccess?.hq_provisioned) {
      return {
        section: "overview",
        label: "Waiting for QikTruck approval",
        hint: "We'll email you when access is active. You can then create keys in Integrations.",
        icon: "mdi-email-check",
      };
    }
    return {
      section: "keys",
      label: "Manage API keys",
      hint: this.state.partnerAccess?.partner_api_live_enabled
        ? "Create sandbox or production keys and revoke old ones from your account."
        : "Create sandbox keys to test. Production keys unlock after QikTruck enables go-live.",
      icon: "mdi-key",
    };
  }

  fetchDomains = () => {
    if (!cookie.load("session-id-qt")) {
      this.setState({ loading: false, error: "Please log in to manage integrations." });
      return;
    }
    axios
      .get(baseURL + "/widget/domains", this.auth())
      .then((res) => this.setState({ loading: false, siteId: res.data?.site_id || null, domains: res.data?.domains || [], domainRequests: res.data?.domain_requests || null }))
      .catch(() => this.setState({ loading: false, error: "Could not load integration settings." }));
  };

  fetchSettings = () => {
    if (!cookie.load("session-id-qt")) return;
    axios
      .get(baseURL + "/widget/settings", this.auth())
      .then((res) => {
        const wh = res.data?.warehouse || {};
        const list = Array.isArray(wh.warehouses) && wh.warehouses.length
          ? wh.warehouses
          : [emptyWarehouse(true)];
        const defaultId = wh.default_warehouse_id || list.find((w) => w.is_default)?.id || list[0]?.id;
        const p = res.data?.packaging || {};
        const b = res.data?.billing || {};
        const next = {
          warehouses: list,
          defaultWarehouseId: defaultId,
          domainRequests: res.data?.domain_requests || null,
          defaultVehicle: p.vehicletype || "",
          defaultHelpers: p.helpers || 0,
          defaultFloors: p.floors || 0,
          allowedVehicles: Array.isArray(p.allowed_vehicles) ? p.allowed_vehicles : [],
          aiPriceTips: p.ai_price_tips !== false,
          defaultCategory: p.category || "",
          defaultPromocode: p.promocode || "",
          billingMode: b.mode || "prepaid_per_order",
          billingEmail: b.email || "",
          billingPaymentPayer: b.payment_payer || "merchant",
        };
        this.setState({
          ...next,
          settingsBaseline: pickSettingsForm({ ...this.state, ...next }),
        });
      })
      .catch(() => {});
  };

  fetchUsage = () => {
    this.setState({ usageLoading: true });
    axios
      .get(baseURL + "/widget/usage", this.auth())
      .then((res) => this.setState({ usageLoading: false, usage: res.data || { total_30d: 0, by_domain: [], by_day: [] } }))
      .catch(() => this.setState({ usageLoading: false, usage: { total_30d: 0, by_domain: [], by_day: [] } }));
  };

  addDomain = (e) => {
    if (e) e.preventDefault();
    const domain = (this.state.newDomain || "").trim();
    if (!domain) return;
    this.setState({ adding: true, error: "", notice: "" });
    axios
      .post(baseURL + "/widget/register-domain", { domain }, this.auth())
      .then((res) => {
        this.setState({ adding: false, newDomain: "", siteId: res.data?.site_id || this.state.siteId, domainRequests: res.data?.domain_requests || this.state.domainRequests, notice: "Domain added. Add the DNS record shown, then Verify." });
        this.fetchDomains();
      })
      .catch((err) => this.setState({ adding: false, error: err?.response?.data?.message || "Could not add domain.", domainRequests: err?.response?.data?.domain_requests || this.state.domainRequests }));
  };

  verifyDomain = (domain) => {
    this.setState({ verifying: domain, error: "", notice: "" });
    axios
      .post(baseURL + "/widget/verify-domain", { domain }, this.auth())
      .then((res) => {
        this.setState({ verifying: "", notice: res.data?.verified ? domain + " is verified and active." : "", error: res.data?.verified ? "" : res.data?.message || "TXT record not found yet." });
        this.fetchDomains();
      })
      .catch((err) => this.setState({ verifying: "", error: err?.response?.data?.message || "Could not verify domain." }));
  };

  toggleStatus = (domain, current) => {
    const status = current === "active" ? "disabled" : "active";
    this.setState({ togglingStatus: domain, error: "", notice: "" });
    axios
      .post(baseURL + "/widget/domain-status", { domain, status }, this.auth())
      .then(() => {
        this.setState({ togglingStatus: "", notice: domain + " is now " + status + "." });
        this.fetchDomains();
      })
      .catch((err) => this.setState({ togglingStatus: "", error: err?.response?.data?.message || "Could not update status." }));
  };

  executeRemoveDomain = (domain) => {
    this.setState({ removing: domain, error: "", notice: "" });
    axios
      .post(baseURL + "/widget/remove-domain", { domain }, this.auth())
      .then((res) => {
        this.setState({
          removing: "",
          confirmModal: null,
          notice: domain + " removed.",
          siteId: res.data?.site_id || null,
        });
        this.fetchDomains();
      })
      .catch((err) =>
        this.setState({
          removing: "",
          confirmModal: null,
          error: err?.response?.data?.message || "Could not remove domain.",
        })
      );
  };

  removeDomain = (domain) => {
    this.askRemoveDomain(domain);
  };

  toggleAllowedVehicle = (id) => {
    this.setState((prev) => {
      const allIds = WIDGET_VEHICLE_OPTIONS.map((v) => v.id);
      let list = prev.allowedVehicles?.length ? [...prev.allowedVehicles] : [...allIds];
      const set = new Set(list);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      const next = [...set];
      if (next.length === allIds.length) return { allowedVehicles: [] };
      return { allowedVehicles: next };
    });
  };

  updateWarehouse = (id, patch) => {
    this.setState((prev) => ({
      warehouses: prev.warehouses.map((w) => (w.id === id ? { ...w, ...patch } : w)),
    }));
  };

  setDefaultWarehouse = (id) => {
    this.setState({ defaultWarehouseId: id, warehouses: this.state.warehouses.map((w) => ({ ...w, is_default: w.id === id })) });
  };

  addWarehouse = () => {
    this.setState((prev) => ({
      warehouses: [...prev.warehouses, emptyWarehouse(prev.warehouses.length === 0)],
    }));
  };

  removeWarehouse = (id) => {
    this.setState((prev) => {
      if (prev.warehouses.length <= 1) return prev;
      const next = prev.warehouses.filter((w) => w.id !== id);
      let defaultId = prev.defaultWarehouseId;
      if (defaultId === id) defaultId = next[0]?.id;
      return {
        warehouses: next.map((w) => ({ ...w, is_default: w.id === defaultId })),
        defaultWarehouseId: defaultId,
      };
    });
  };

  saveSettings = (e) => {
    if (e) e.preventDefault();
    this.setState({ settingsSaving: true, settingsNotice: "", error: "" });
    const defaultId =
      this.state.defaultWarehouseId ||
      this.state.warehouses.find((w) => w.is_default)?.id ||
      this.state.warehouses[0]?.id;
    const payload = {
      warehouse: {
        warehouses: this.state.warehouses,
        default_warehouse_id: defaultId,
      },
      packaging: {
        vehicletype: this.state.defaultVehicle,
        helpers: Number(this.state.defaultHelpers) || 0,
        floors: Number(this.state.defaultFloors) || 0,
        category: this.state.defaultCategory,
        promocode: this.state.defaultPromocode,
        allowed_vehicles: this.state.allowedVehicles || [],
        ai_price_tips: this.state.aiPriceTips !== false,
      },
      billing: {
        mode: this.state.billingMode,
        email: this.state.billingEmail,
        payment_payer: this.state.billingPaymentPayer || "merchant",
      },
    };
    axios
      .put(baseURL + "/widget/settings", payload, this.auth())
      .then((res) => {
        const located = (res.data?.warehouses_located || 0) > 0;
        this.setState({
          settingsSaving: false,
          settingsNotice: located
            ? "Saved. " + (res.data?.warehouses_located || 0) + " pickup location(s) confirmed."
            : "Saved, but we could not locate every address - pick from the autocomplete suggestions.",
        });
        this.fetchSettings();
      })
      .catch((err) => this.setState({ settingsSaving: false, error: err?.response?.data?.message || "Could not save settings." }));
  };

  isSettingsDirty = () => {
    const baseline = this.state.settingsBaseline;
    if (!baseline) return false;
    return !settingsFormEqual(pickSettingsForm(this.state), baseline);
  };

  discardSettings = () => {
    const b = this.state.settingsBaseline;
    if (!b) return;
    this.setState({
      warehouses: b.warehouses.map((w) => ({ ...w })),
      defaultWarehouseId: b.defaultWarehouseId,
      defaultVehicle: b.defaultVehicle,
      defaultHelpers: b.defaultHelpers,
      defaultFloors: b.defaultFloors,
      allowedVehicles: [...b.allowedVehicles],
      aiPriceTips: b.aiPriceTips,
      defaultCategory: b.defaultCategory,
      defaultPromocode: b.defaultPromocode,
      billingMode: b.billingMode,
      billingEmail: b.billingEmail,
      billingPaymentPayer: b.billingPaymentPayer || "merchant",
      settingsNotice: "",
      error: "",
    });
  };

  htmlSnippet() {
    const site = this.state.siteId || "qt_pub_yourSiteId";
    return '<script src="https://cdn.qiktruck.co.za/widget.js"\n        data-qt-site="' + site + '"\n        data-qt-mode="bubble"\n        data-qt-label="Get a delivery price"\n        data-qt-description="Your product name"></script>';
  }
  reactSnippet() {
    const site = this.state.siteId || "qt_pub_yourSiteId";
    return 'import { QikTruckQuoteButton } from "@qiktruck/widget/react";\n\n<QikTruckQuoteButton\n  site="' + site + '"\n  label="Get a delivery price"\n/>';
  }
  inlineSnippet() {
    const site = this.state.siteId || "qt_pub_yourSiteId";
    return '<div data-qt-widget></div>\n<script src="https://cdn.qiktruck.co.za/widget.js"\n        data-qt-site="' + site + '"\n        data-qt-mode="inline"></script>';
  }

  copy = (which, text) => {
    try {
      navigator.clipboard.writeText(text);
      this.setState({ copied: which });
      setTimeout(() => this.setState({ copied: "" }), 2000);
    } catch (e) { /* manual select */ }
  };

  preview = () => {
    const site = this.state.siteId || "";
    const params = new URLSearchParams();
    if (site) params.set("site", site);
    params.set("description", "3-Seater Couch");
    params.set("deliverylat", "-26.0935");
    params.set("deliverylng", "27.9983");
    params.set("deliveryaddress", "Randburg, South Africa");
    params.set("email", "shopper@example.com");
    params.set("mobile", "0821234567");
    params.set("trucktype", "1tonopen");
    const feOrigin =
      typeof window !== "undefined" && window.location.hostname === "localhost"
        ? "http://localhost:3008"
        : "https://qiktruck.co.za";
    window.open(feOrigin + "/widget/quote?" + params.toString(), "_blank", "noopener");
  };

  steps() {
    const domainActive = this.state.domains.some((d) => d.status === "active");
    const pickupSet = this.state.warehouses.some((w) => w.lat != null && w.lng != null);
    const hasSnippet = !!this.state.siteId;
    return { domainActive, pickupSet, hasSnippet, installable: domainActive && pickupSet };
  }

  setupProgress() {
    const st = this.steps();
    const items = [
      { id: "domains", label: "Domain verified", done: st.domainActive },
      { id: "config", label: "Pickup configured", done: st.pickupSet },
      { id: "install", label: "Ready to embed", done: st.installable },
    ];
    const completed = items.filter((x) => x.done).length;
    return {
      items,
      completed,
      total: items.length,
      pct: Math.round((completed / items.length) * 100),
      live: st.installable,
    };
  }

  nextSetupStep() {
    const st = this.steps();
    if (!st.domainActive) {
      return { section: "domains", label: "Verify your domain", hint: "Add a DNS record to authorise your store.", icon: "mdi-web" };
    }
    if (!st.pickupSet) {
      return { section: "config", label: "Set warehouse pickup", hint: "Tell us where drivers collect from.", icon: "mdi-store" };
    }
    if (st.installable) {
      return { section: "install", label: "Paste embed code", hint: "One snippet in your theme - then preview.", icon: "mdi-code-tags" };
    }
    return { section: "install", label: "Preview shopper flow", hint: "Walk through quote → book like a customer.", icon: "mdi-eye", action: this.preview };
  }

  sectionHead(icon, iconClass, title, desc) {
    return (
      <div className="qt-section-head">
        <div className={"qt-section-icon qt-section-icon--" + iconClass}>
          <i className={"mdi " + icon} />
        </div>
        <div>
          <h4 className="qt-section-title">{title}</h4>
          <p className="qt-section-desc">{desc}</p>
        </div>
      </div>
    );
  }

  tip(variant, icon, children) {
    return (
      <div className={"qt-tip qt-tip--" + variant}>
        <i className={"mdi " + icon} />
        <div>{children}</div>
      </div>
    );
  }

  domainStatus(status) {
    const map = {
      active: { cls: "active", icon: "mdi-check-circle", label: "Active" },
      pending: { cls: "pending", icon: "mdi-clock-outline", label: "Pending verification" },
      disabled: { cls: "disabled", icon: "mdi-pause-circle", label: "Disabled" },
    };
    const s = map[status] || map.pending;
    return (
      <span className={"qt-status qt-status--" + s.cls}>
        <i className={"mdi " + s.icon} /> {s.label}
      </span>
    );
  }

  codeBlock(text, key) {
    return (
      <div className="qt-code-wrap">
        <pre className="qt-code">{text}</pre>
        <button type="button" className="qt-copy-btn" onClick={() => this.copy(key, text)}>
          <i className={"mdi " + (this.state.copied === key ? "mdi-check" : "mdi-content-copy")} />
          {" "}{this.state.copied === key ? "Copied" : "Copy"}
        </button>
      </div>
    );
  }

  renderDnsVerification(domain, verification) {
    const host = verification.host || "";
    const value = verification.value || "";
    const hostKey = "dns-host-" + domain;
    const valKey = "dns-val-" + domain;
    return (
      <div className="mt-3">
        <div className="qt-dns-record">
          <div className="qt-dns-row">
            <span className="qt-dns-label">Type</span>
            <code>TXT</code>
          </div>
          <div className="qt-dns-row">
            <span className="qt-dns-label">Host / Name</span>
            <code className="qt-dns-value">{host}</code>
            <button type="button" className="btn btn-sm btn-outline-light qt-dns-copy" onClick={() => this.copy(hostKey, host)}>
              <i className={"mdi " + (this.state.copied === hostKey ? "mdi-check" : "mdi-content-copy")} />
            </button>
          </div>
          <div className="qt-dns-row">
            <span className="qt-dns-label">Value</span>
            <code className="qt-dns-value">{value}</code>
            <button type="button" className="btn btn-sm btn-outline-light qt-dns-copy" onClick={() => this.copy(valKey, value)}>
              <i className={"mdi " + (this.state.copied === valKey ? "mdi-check" : "mdi-content-copy")} />
            </button>
          </div>
        </div>

        <p className="small text-muted mt-3 mb-2">
          <i className="mdi mdi-clock-outline me-1" />
          DNS can take 2-30 minutes to propagate. Then click <strong>Verify DNS</strong> above.
        </p>

        <h6 className="small fw-bold mb-2">
          <i className="mdi mdi-book-open-page-variant me-1" style={{ color: "#f17516" }} />
          Provider guides
        </h6>
        <div className="qt-dns-guides">
          {DNS_GUIDES.map((g) => (
            <details key={g.id} className="qt-dns-guide">
              <summary>
                <i className={"mdi " + g.icon + " me-2"} />
                {g.name}
                <i className="mdi mdi-chevron-down qt-dns-guide-chevron" />
              </summary>
              <ol className="small mb-0 ps-3">
                {g.steps.map((step, i) => (
                  <li key={i} className="mb-1">{step}</li>
                ))}
              </ol>
            </details>
          ))}
        </div>
      </div>
    );
  }

  // ---------- sections ----------
  renderTrustStrip() {
    return (
      <div className="qt-trust-strip">
        {[
          { i: "mdi-shield-lock", t: "Domain-verified embed" },
          { i: "mdi-credit-card-off", t: "No card data on your site" },
          { i: "mdi-truck-check", t: "QikTruck fulfils delivery" },
          { i: "mdi-clock-fast", t: "Quotes in seconds" },
        ].map((x) => (
          <span key={x.t} className="qt-trust-item">
            <i className={"mdi " + x.i} />
            {x.t}
          </span>
        ))}
      </div>
    );
  }

  renderFleetPreview = ({ size = "sm", label, ids } = {}) => {
    const vehicles = ids
      ? WIDGET_VEHICLE_OPTIONS.filter((v) => ids.includes(v.id))
      : WIDGET_VEHICLE_OPTIONS;
    if (!vehicles.length) return null;
    return (
      <div className={"qt-fleet-preview qt-fleet-preview--" + size}>
        {label ? <span className="qt-fleet-preview-label">{label}</span> : null}
        <div className="qt-fleet-preview-row">
          {vehicles.map((v) => (
            <span key={v.id} className="qt-fleet-preview-item" title={v.label}>
              <img src={truckImageUrl(v.id)} alt="" loading="lazy" />
              <span>{v.short || v.label}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  renderPhonePreview() {
    return <WidgetEmbedMock stage="quote" size="lg" showBubble tilt />;
  }

  renderPartnerApiMock() {
    return <PartnerApiStageMock stage="quote" size="lg" />;
  }

  renderPartnerStatusBar() {
    const prog = this.partnerSetupProgress();
    const next = this.nextPartnerSetupStep();
    if (this.state.activeSection !== "overview" && prog.submitted) return null;
    return (
      <div className={"qt-status-bar" + (prog.submitted ? " qt-status-bar--live" : "")}>
        <div className="qt-status-bar-progress">
          <div className="qt-status-ring" style={{ "--qt-pct": prog.pct }}>
            <span>{prog.pct}%</span>
          </div>
          <div>
            <div className="qt-status-bar-label">
              {prog.submitted ? (
                <><i className="mdi mdi-email-check me-1" />Application submitted</>
              ) : (
                <>Partner API onboarding</>
              )}
            </div>
            <div className="qt-status-bar-hint">{next.hint}</div>
          </div>
        </div>
        {!prog.submitted ? (
          <button
            type="button"
            className="btn btn-primary btn-sm qt-status-bar-cta"
            onClick={() => this.go("apply")}
          >
            <i className={"mdi " + next.icon + " me-1"} />
            {next.label}
            <i className="mdi mdi-arrow-right ms-1" />
          </button>
        ) : (
          <div className="d-flex gap-2 flex-wrap">
            {this.state.partnerAccess?.hq_provisioned ? (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => this.go("keys")}>
                <i className="mdi mdi-key me-1" />Manage API keys
              </button>
            ) : null}
            <a href={DOCS_PATH} className="btn btn-primary btn-sm">
              <i className="mdi mdi-book-open-page-variant me-1" />Full API docs
            </a>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => this.go("docs")}>
              <i className="mdi mdi-api me-1" />Quick reference
            </button>
          </div>
        )}
      </div>
    );
  }

  renderPartnerSidebarProgress() {
    const prog = this.partnerSetupProgress();
    return (
      <div className="qt-sidebar-progress">
        <div className="qt-sidebar-progress-head">
          <span>Onboarding</span>
          <strong>{prog.completed}/{prog.total}</strong>
        </div>
        <div className="qt-sidebar-progress-track">
          <div className="qt-sidebar-progress-fill" style={{ width: prog.pct + "%" }} />
        </div>
        <ul className="qt-sidebar-checklist">
          {prog.items.map((item) => (
            <li key={item.id} className={item.done ? "is-done" : ""}>
              <i className={"mdi " + (item.done ? "mdi-check-circle" : "mdi-circle-outline")} />
              {item.label}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderWidgetStatusBar() {
    const prog = this.setupProgress();
    const next = this.nextSetupStep();
    if (prog.live && this.state.activeSection !== "overview") return null;
    return (
      <div className={"qt-status-bar" + (prog.live ? " qt-status-bar--live" : "")}>
        <div className="qt-status-bar-progress">
          <div className="qt-status-ring" style={{ "--qt-pct": prog.pct }}>
            <span>{prog.pct}%</span>
          </div>
          <div>
            <div className="qt-status-bar-label">
              {prog.live ? (
                <><i className="mdi mdi-check-circle me-1" />Ready to go live</>
              ) : (
                <>Setup progress</>
              )}
            </div>
            <div className="qt-status-bar-hint">{next.hint}</div>
          </div>
        </div>
        {!prog.live && (
          <button
            type="button"
            className="btn btn-primary btn-sm qt-status-bar-cta"
            onClick={next.action || (() => this.go(next.section))}
          >
            <i className={"mdi " + next.icon + " me-1"} />
            {next.label}
            <i className="mdi mdi-arrow-right ms-1" />
          </button>
        )}
        {prog.live && (
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => this.go("install")}>
              <i className="mdi mdi-code-tags me-1" />Get snippet
            </button>
            <button type="button" className="btn btn-success btn-sm" onClick={this.preview}>
              <i className="mdi mdi-eye me-1" />Preview
            </button>
          </div>
        )}
      </div>
    );
  }

  renderSidebarProgress() {
    const prog = this.setupProgress();
    return (
      <div className="qt-sidebar-progress">
        <div className="qt-sidebar-progress-head">
          <span>Setup</span>
          <strong>{prog.completed}/{prog.total}</strong>
        </div>
        <div className="qt-sidebar-progress-track">
          <div className="qt-sidebar-progress-fill" style={{ width: prog.pct + "%" }} />
        </div>
        <ul className="qt-sidebar-checklist">
          {prog.items.map((item) => (
            <li key={item.id} className={item.done ? "is-done" : ""}>
              <i className={"mdi " + (item.done ? "mdi-check-circle" : "mdi-circle-outline")} />
              {item.label}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderLoadingSkeleton() {
    return (
      <div className="qt-skeleton-wrap" aria-busy="true" aria-label="Loading">
        <div className="qt-skeleton qt-skeleton--bar" />
        <Row className="g-3 mt-1">
          <Col lg={3} className="d-none d-lg-block">
            <div className="qt-skeleton qt-skeleton--sidebar" />
          </Col>
          <Col lg={9}>
            <div className="qt-skeleton qt-skeleton--hero" />
            <div className="qt-skeleton qt-skeleton--card mt-3" />
          </Col>
        </Row>
      </div>
    );
  }

  renderOverview() {
    const st = this.steps();
    const prog = this.setupProgress();
    const u = this.state.usage;
    const next = this.nextSetupStep();
    return (
      <>
        {this.renderTrustStrip()}
        <Card className="mb-4 border-0 qt-hero qt-hero--split">
          <CardBody className="py-4">
            <Row className="align-items-center g-4">
              <Col lg={7}>
                <div className="qt-hero-eyebrow">
                  {prog.live ? (
                    <span className="qt-live-badge"><span className="qt-live-dot" /> Live-ready</span>
                  ) : (
                    <span>Website widget</span>
                  )}
                </div>
                <h3 className="mb-2">Delivery quotes on your store, like Uber - but for trucks</h3>
                <p className="qt-hero-lead mb-3">
                  Shoppers see a real all-in price for their address. They book and pay on QikTruck -
                  you never touch card data or dispatch drivers.
                </p>
                <div className="qt-hero-pills">
                  <span className="qt-hero-pill"><i className="mdi mdi-shield-check" /> Domain-locked</span>
                  <span className="qt-hero-pill"><i className="mdi mdi-script-text" /> One script tag</span>
                  <span className="qt-hero-pill"><i className="mdi mdi-timer-sand" /> ~5 min to go live</span>
                </div>
                {this.renderFleetPreview({
                  size: "xs",
                  label: "Bakkies & trucks shoppers can book",
                })}
                {!prog.live && (
                  <button
                    type="button"
                    className="btn btn-primary mt-3"
                    onClick={next.action || (() => this.go(next.section))}
                  >
                    <i className={"mdi " + next.icon + " me-1"} />
                    Continue setup - {next.label}
                  </button>
                )}
              </Col>
              <Col lg={5} className="d-flex justify-content-lg-end justify-content-center">
                {this.renderPhonePreview()}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {u && u.total_30d > 0 && (
          <Row className="g-3 mb-4">
            <Col sm={4}>
              <div className="qt-stat-card">
                <div className="qt-stat-value">{u.total_30d}</div>
                <div className="qt-stat-label">Widget loads (30d)</div>
              </div>
            </Col>
            <Col sm={4}>
              <div className="qt-stat-card">
                <div className="qt-stat-value">{this.state.domains.filter((d) => d.status === "active").length}</div>
                <div className="qt-stat-label">Active domains</div>
              </div>
            </Col>
            <Col sm={4}>
              <div className="qt-stat-card">
                <div className="qt-stat-value">{prog.pct}%</div>
                <div className="qt-stat-label">Setup complete</div>
              </div>
            </Col>
          </Row>
        )}

        <div className="qt-card mb-4">
          <div className="qt-card-body">
            <h5 className="mb-1 qt-card-heading">
              <i className="mdi mdi-transit-connection-variant me-2" />
              Shopper journey
            </h5>
            <p className="text-muted small mb-3">
              One script tag on your site. Quote, checkout, and tracking run on QikTruck - your product page stays clean.
            </p>
            <EmbedFlowStrip stages={WIDGET_EMBED_STAGES} showBubbleOnQuote />
          </div>
        </div>

        <div className="qt-card">
          <div className="qt-card-body">
            <h5 className="mb-1 qt-card-heading">
              <i className="mdi mdi-format-list-checks me-2" />
              Setup checklist
            </h5>
            <p className="text-muted small mb-3">Most merchants finish in under 10 minutes.</p>
            {[
              { done: st.domainActive, icon: "mdi-web", t: "Verify your website", h: "DNS TXT record proves the widget is allowed on your domain.", s: "domains", a: "Add domain", btn: "btn-primary" },
              { done: st.pickupSet, icon: "mdi-store", t: "Set warehouse pickup", h: "Shoppers only enter delivery - we collect from your store.", s: "config", a: "Set pickup", btn: "btn-primary" },
              { done: st.installable, icon: "mdi-code-tags", t: "Paste embed code", h: "One script in your theme footer or tag manager.", s: "install", a: "Get snippet", btn: "btn-outline-primary" },
              { done: false, icon: "mdi-eye", t: "Test shopper experience", h: "Preview the quote flow exactly as customers see it.", s: "install", a: "Preview", btn: "btn-success", action: () => this.preview() },
            ].map((step, i) => (
              <div key={i} className={"qt-setup-step" + (step.done ? " is-done" : "")}>
                <div className="qt-setup-num">
                  {step.done ? <i className="mdi mdi-check" /> : i + 1}
                </div>
                <div className="qt-setup-body">
                  <div className="qt-setup-title">
                    <i className={"mdi " + step.icon + " me-1"} />
                    {step.t}
                  </div>
                  <p className="qt-setup-hint">{step.h}</p>
                </div>
                <div className="qt-setup-action">
                  <button
                    type="button"
                    className={"btn btn-sm " + step.btn}
                    onClick={step.action || (() => this.go(step.s))}
                  >
                    {step.a}
                    <i className="mdi mdi-arrow-right ms-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  renderDomains() {
    const s = this.state;
    return (
      <div className="qt-card">
        <div className="qt-card-body">
          {this.sectionHead(
            "mdi-web",
            "orange",
            "Allowed websites",
            "Only verified domains can load your widget. This replaces API keys - your site id is public, your domain is the lock."
          )}
          {this.tip("info", "mdi-information-outline", (
            <>
              <strong>Tip:</strong> Add both <code>yourstore.co.za</code> and <code>www.yourstore.co.za</code> if shoppers use either.
              DNS changes can take up to 30 minutes to propagate.
            </>
          ))}
          {s.domainRequests && !s.domainRequests.unlimited && (
            <Alert color={s.domainRequests.remaining > 0 ? "light" : "warning"} className="mb-3">
              <i className="mdi mdi-shield-check-outline me-1" />
              Domain registrations today: <strong>{s.domainRequests.used_today}</strong> / {s.domainRequests.limit}
              {s.domainRequests.remaining <= 0
                ? " - daily limit reached. Contact QikTruck support for more."
                : " remaining."}
            </Alert>
          )}
          <form onSubmit={this.addDomain} className="mb-4">
            <label className="form-label fw-semibold small">
              <i className="mdi mdi-plus-circle-outline me-1" style={{ color: "#f17516" }} />
              Add a domain
            </label>
            <Row className="g-2">
              <Col md={8}>
                <input
                  className="form-control"
                  placeholder="yourstore.co.za"
                  value={s.newDomain}
                  onChange={(e) => this.setState({ newDomain: e.target.value, error: "" })}
                />
              </Col>
              <Col md={4}>
                <button className="btn btn-primary w-100" type="submit" disabled={s.adding}>
                  {s.adding ? <Spinner size="sm" /> : <><i className="mdi mdi-plus me-1" />Add domain</>}
                </button>
              </Col>
            </Row>
          </form>
          {s.domains.length === 0 && (
            <div className="qt-empty">
              <i className="mdi mdi-web" />
              <p className="mb-2"><strong>No domains yet</strong></p>
              <p className="small mb-0">Add the website where your widget will appear - e.g. your WooCommerce or Shopify store URL.</p>
            </div>
          )}
          {s.domains.map((d) => (
            <div
              key={d.domain}
              className={
                "qt-domain" +
                (d.status === "active" ? " is-active" : d.status === "pending" ? " is-pending" : "")
              }
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <i className="mdi mdi-web me-1" style={{ color: "#4f6367" }} />
                  <strong>{d.domain}</strong>
                  <span className="ms-2">{this.domainStatus(d.status)}</span>
                </div>
                <div className="d-flex gap-2 flex-wrap qt-domain-actions">
                  {d.status === "pending" && (
                    <button className="btn btn-sm btn-primary" onClick={() => this.verifyDomain(d.domain)} disabled={s.verifying === d.domain}>
                      {s.verifying === d.domain ? <Spinner size="sm" /> : <><i className="mdi mdi-dns me-1" />Verify DNS</>}
                    </button>
                  )}
                  {d.status !== "pending" && (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => this.toggleStatus(d.domain, d.status)} disabled={s.togglingStatus === d.domain}>
                      {s.togglingStatus === d.domain ? <Spinner size="sm" /> : <>
                        <i className={"mdi " + (d.status === "active" ? "mdi-pause" : "mdi-play") + " me-1"} />
                        {d.status === "active" ? "Disable" : "Enable"}
                      </>}
                    </button>
                  )}
                  <button className="btn btn-sm btn-outline-danger" onClick={() => this.removeDomain(d.domain)} disabled={s.removing === d.domain}>
                    {s.removing === d.domain ? <Spinner size="sm" /> : <><i className="mdi mdi-delete-outline me-1" />Remove</>}
                  </button>
                </div>
              </div>
              {d.status === "pending" && d.verification && this.renderDnsVerification(d.domain, d.verification)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderConfig() {
    const s = this.state;
    const dirty = this.isSettingsDirty();
    return (
      <div className="qt-card">
        <div className="qt-card-body">
          {this.sectionHead(
            "mdi-cog",
            "teal",
            "Store settings",
            "Configure once here - the widget picks up pickup location, vehicle defaults and promos automatically on every page."
          )}
          {s.settingsNotice && !dirty && (
            <Alert color="success"><i className="mdi mdi-check-circle me-1" />{s.settingsNotice}</Alert>
          )}
          {dirty && (
            <Alert color="warning" className="qt-settings-dirty-alert">
              <i className="mdi mdi-circle-medium me-1" />
              <strong>Unsaved changes</strong> - save your settings so the widget uses the latest pickup locations and defaults.
            </Alert>
          )}
          <form
            onSubmit={this.saveSettings}
            className={dirty ? "qt-settings-form--dirty" : ""}
          >
            <div className="qt-config-block">
              <div className="qt-config-block-head">
                <i className="mdi mdi-store" />
                <h6>Pickup warehouses</h6>
              </div>
              <p>
                Where drivers collect goods. Add one or more locations (e.g. Cape Town + Johannesburg).
                Shoppers only enter their <strong>delivery address</strong>. For multi-store sites, pass{" "}
                <code>warehouse=wh_…</code> on the widget URL or use per-page collection coordinates.
              </p>
              <div className="qt-warehouse-list">
                {s.warehouses.map((wh, idx) => {
                  const isDefault = wh.id === s.defaultWarehouseId || wh.is_default;
                  const isLocated = wh.lat != null && wh.lng != null;
                  return (
                    <div key={wh.id} className="qt-warehouse-card">
                      <div className="qt-warehouse-card__header">
                        <div className="qt-warehouse-card__index" aria-hidden="true">{idx + 1}</div>
                        <div className="qt-warehouse-card__name">
                          <label className="form-label small fw-semibold mb-1">Location name</label>
                          <input
                            className="form-control"
                            placeholder={idx === 0 ? "Main warehouse" : "Branch name"}
                            value={wh.label || ""}
                            onChange={(e) => this.updateWarehouse(wh.id, { label: e.target.value })}
                          />
                        </div>
                        <div className="qt-warehouse-card__actions">
                          <button
                            type="button"
                            className={
                              "btn btn-sm " + (isDefault ? "btn-primary" : "btn-outline-secondary")
                            }
                            onClick={() => this.setDefaultWarehouse(wh.id)}
                          >
                            {isDefault ? "Default" : "Set default"}
                          </button>
                          {s.warehouses.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => this.removeWarehouse(wh.id)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="qt-warehouse-card__address">
                        <div className="qt-warehouse-card__address-label">
                          <label className="form-label small fw-semibold mb-1">Street address</label>
                          {isLocated && (
                            <span className="qt-warehouse-located">
                              <i className="mdi mdi-map-marker-check" /> Located
                            </span>
                          )}
                        </div>
                        <PlacesAutocomplete
                          type="text"
                          className="form-control"
                          id={"warehouse-" + wh.id}
                          placeholder="Search address in South Africa…"
                          defaultValue={wh.address || ""}
                          onInputChange={(val) =>
                            this.updateWarehouse(wh.id, { address: val, lat: null, lng: null })
                          }
                          onSelect={(loc) =>
                            this.updateWarehouse(wh.id, {
                              address: loc.address || "",
                              lat: loc.lat,
                              lng: loc.lng,
                            })
                          }
                        />
                      </div>
                      {wh.id && (
                        <div className="qt-warehouse-card__foot">
                          Widget param: <code>warehouse={wh.id}</code>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={this.addWarehouse}>
                <i className="mdi mdi-plus me-1" />Add warehouse
              </button>
            </div>

            <div className="qt-config-block">
              <div className="qt-config-block-head">
                <i className="mdi mdi-package-variant" />
                <h6>Delivery defaults</h6>
              </div>
              <p>
                Set what shoppers see in the compact checkout. Helpers and floors are merchant-controlled - buyers
                cannot change them. Collection is always ASAP from your warehouse.
              </p>
              <Row className="g-2">
                <Col md={4}>
                  <label className="form-label small fw-semibold">Default vehicle</label>
                  <div className="qt-default-vehicle">
                    {s.defaultVehicle ? (
                      <img
                        src={truckImageUrl(s.defaultVehicle)}
                        alt=""
                        className="qt-vehicle-thumb-sm"
                        loading="lazy"
                      />
                    ) : null}
                    <select
                      className="form-select"
                      value={s.defaultVehicle}
                      onChange={(e) => this.setState({ defaultVehicle: e.target.value })}
                    >
                      <option value="">First allowed option</option>
                      {WIDGET_VEHICLE_OPTIONS.map((v) => (
                        <option key={v.id} value={v.id}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                </Col>
                <Col md={4}>
                  <label className="form-label small fw-semibold">Included helpers</label>
                  <input type="number" min="0" max="4" className="form-control" value={s.defaultHelpers} onChange={(e) => this.setState({ defaultHelpers: e.target.value })} />
                  <span className="form-text">Not shown to shoppers - baked into price.</span>
                </Col>
                <Col md={4}>
                  <label className="form-label small fw-semibold">Delivery floors</label>
                  <input type="number" min="0" max="20" className="form-control" value={s.defaultFloors} onChange={(e) => this.setState({ defaultFloors: e.target.value })} />
                </Col>
                <Col md={4}>
                  <label className="form-label small fw-semibold">Load category</label>
                  <select className="form-select" value={s.defaultCategory} onChange={(e) => this.setState({ defaultCategory: e.target.value })}>
                    <option value="">General goods</option>
                    <option value="handheld-items">Handheld / boxes</option>
                    <option value="furniture">Furniture</option>
                    <option value="appliances">Appliances</option>
                  </select>
                </Col>
              </Row>
            </div>

            <div className="qt-config-block">
              <div className="qt-config-block-head">
                <i className="mdi mdi-truck-outline" />
                <h6>Vehicle options for shoppers</h6>
              </div>
              <p>
                Choose which vehicles appear in the embed. Leave all unchecked to show every option. You can limit
                choices by plan later - fewer options keeps checkout faster.
              </p>
              <div className="qt-vehicle-picks">
                {WIDGET_VEHICLE_OPTIONS.map((v) => {
                  const effective =
                    s.allowedVehicles?.length > 0
                      ? s.allowedVehicles
                      : WIDGET_VEHICLE_OPTIONS.map((x) => x.id);
                  const checked = effective.includes(v.id);
                  return (
                    <label
                      key={v.id}
                      className={"qt-vehicle-pick" + (checked ? " is-checked" : "")}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => this.toggleAllowedVehicle(v.id)}
                      />
                      <img
                        src={truckImageUrl(v.id)}
                        alt=""
                        className="qt-vehicle-pick-img"
                        loading="lazy"
                      />
                      <span className="qt-vehicle-pick-label">{v.label}</span>
                    </label>
                  );
                })}
              </div>
              {this.renderFleetPreview({
                size: "sm",
                label: "Preview - what shoppers see in the price step",
                ids:
                  s.allowedVehicles?.length > 0
                    ? s.allowedVehicles
                    : WIDGET_VEHICLE_OPTIONS.map((x) => x.id),
              })}
              <p className="form-text mb-0">
                {s.allowedVehicles?.length
                  ? s.allowedVehicles.length + " vehicle(s) selected."
                  : "All vehicles available to shoppers."}
              </p>
            </div>

            <div className="qt-config-block">
              <div className="qt-config-block-head">
                <i className="mdi mdi-lightbulb-on-outline" />
                <h6>AI load tips</h6>
              </div>
              <p>After a price is shown, a single compact tip can suggest a better vehicle fit (backed by the quoted price).</p>
              <label className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={s.aiPriceTips !== false}
                  onChange={(e) => this.setState({ aiPriceTips: e.target.checked })}
                />
                <span className="form-check-label">Show AI tips after pricing</span>
              </label>
            </div>

            <div className="qt-config-block">
              <div className="qt-config-block-head">
                <i className="mdi mdi-tag-outline" />
                <h6>Delivery discount</h6>
              </div>
              <p>Optional promo code applied automatically so shoppers see a lower delivery price at checkout.</p>
              <Row className="g-2">
                <Col md={6}>
                  <input className="form-control" placeholder="e.g. HOMESTYLE10" value={s.defaultPromocode} onChange={(e) => this.setState({ defaultPromocode: e.target.value.toUpperCase() })} />
                </Col>
              </Row>
            </div>

            <div className="qt-config-block">
              <div className="qt-config-block-head">
                <i className="mdi mdi-credit-card-outline" />
                <h6>Billing</h6>
              </div>
              <p>How your business pays QikTruck for delivery trips (widget and Partner API default).</p>
              <Row className="g-2">
                <Col md={6}>
                  <label className="form-label small fw-semibold">Settlement with QikTruck</label>
                  <select className="form-select" value={s.billingMode} onChange={(e) => this.setState({ billingMode: e.target.value })}>
                    <option value="prepaid_per_order">Prepaid - charge per delivery</option>
                    <option value="postpaid_invoice">Postpaid - weekly invoice</option>
                  </select>
                </Col>
                <Col md={6}>
                  <label className="form-label small fw-semibold">Default who pays QikTruck</label>
                  <select
                    className="form-select"
                    value={s.billingPaymentPayer || "merchant"}
                    onChange={(e) => this.setState({ billingPaymentPayer: e.target.value })}
                  >
                    <option value="merchant">Merchant account (default)</option>
                    <option value="end_customer">End customer via PayFast</option>
                  </select>
                </Col>
                <Col md={6}>
                  <label className="form-label small fw-semibold">Accounts email</label>
                  <input type="email" className="form-control" placeholder="accounts@yourstore.co.za" value={s.billingEmail} onChange={(e) => this.setState({ billingEmail: e.target.value })} />
                </Col>
              </Row>
            </div>

            {dirty ? (
              <div className="qt-settings-save-bar">
                <div className="qt-settings-save-bar__msg">
                  <span className="qt-settings-save-bar__dot" aria-hidden="true" />
                  Unsaved changes
                </div>
                <div className="qt-settings-save-bar__actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={this.discardSettings}
                    disabled={s.settingsSaving}
                  >
                    Discard
                  </button>
                  <button className="btn btn-sm btn-primary qt-settings-save-bar__save" type="submit" disabled={s.settingsSaving}>
                    {s.settingsSaving ? <Spinner size="sm" /> : <><i className="mdi mdi-content-save me-1" />Save settings</>}
                  </button>
                </div>
              </div>
            ) : (
              <button className="btn btn-primary" type="submit" disabled={s.settingsSaving}>
                {s.settingsSaving ? <Spinner size="sm" /> : <><i className="mdi mdi-content-save me-1" />Save settings</>}
              </button>
            )}
          </form>
        </div>
      </div>
    );
  }

  renderInstall() {
    const s = this.state;
    return (
      <div className="qt-card">
        <div className="qt-card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
            {this.sectionHead(
              "mdi-code-tags",
              "mint",
              "Embed on your site",
              "Copy one snippet into your theme footer, HTML template, or tag manager. Works on any platform."
            )}
            <button type="button" className="btn btn-success btn-sm align-self-start" onClick={this.preview}>
              <i className="mdi mdi-eye me-1" />Preview shopper view
            </button>
          </div>
          {this.tip("warn", "mdi-alert-circle-outline", (
            <>
              <strong>Before you paste:</strong> verify at least one domain and save your warehouse pickup in Configuration.
              Your site id: <code>{s.siteId || "(add a domain first)"}</code>
            </>
          ))}
          <Nav tabs className="mb-3 border-0">
            {[
              ["html", "mdi-language-html5", "Any website"],
              ["react", "mdi-react", "React / Next.js"],
              ["inline", "mdi-page-layout", "Inline embed"],
            ].map(([k, ic, lbl]) => (
              <NavItem key={k}>
                <NavLink className={classnames({ active: s.installTab === k })} onClick={() => this.setState({ installTab: k })} style={{ cursor: "pointer" }}>
                  <i className={"mdi " + ic + " me-1"} />{lbl}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
          <TabContent activeTab={s.installTab}>
            <TabPane tabId="html">
              <p className="small text-muted mb-2">
                <strong>1.</strong> Paste before <code>&lt;/body&gt;</code> on every page (or your theme footer).
                <br />
                <strong>2.</strong> Optional: add <code>data-qt-open</code> to your own button to trigger the quote popup.
              </p>
              {this.codeBlock(this.htmlSnippet(), "html")}
            </TabPane>
            <TabPane tabId="react">
              <p className="small text-muted mb-2">Install the package, then drop the component on product pages.</p>
              {this.codeBlock(this.reactSnippet(), "react")}
              <p className="text-muted small mt-2 mb-0">Run <code>npm i @qiktruck/widget</code> when the package is published.</p>
            </TabPane>
            <TabPane tabId="inline">
              <p className="small text-muted mb-2">Renders the quote form directly in the page - no popup. Good for dedicated delivery pages.</p>
              {this.codeBlock(this.inlineSnippet(), "inline")}
            </TabPane>
          </TabContent>

          <div className="qt-embed-compare mt-4">
            <div className="qt-embed-compare__head">
              <h6 className="small fw-bold mb-1">
                <i className="mdi mdi-resize-bottom-right me-1" style={{ color: "#f17516" }} />
                Fits anywhere
              </h6>
              <p className="text-muted small mb-0">
                Floating bubble on product pages, or a bare card inline - same hosted checkout either way.
              </p>
            </div>
            <div className="qt-embed-compare__row">
              <div className="qt-embed-compare__item">
                <WidgetEmbedMock stage="quote" size="sm" showBubble />
                <span className="qt-embed-compare__label">Bubble trigger</span>
              </div>
              <div className="qt-embed-compare__item">
                <WidgetEmbedMock stage="priced" size="sm" frame="card" />
                <span className="qt-embed-compare__label">Inline card</span>
              </div>
            </div>
          </div>

          <h6 className="qt-install-platforms-head mt-4 mb-3">
            <i className="mdi mdi-store me-1" />
            Platform-specific instructions
          </h6>
          <Row className="g-3">
            {PLATFORM_INSTALLS.map((p) => (
              <Col md={4} key={p.id}>
                <div className="qt-platform-card">
                  <div className="qt-platform-icon">
                    <i className={"mdi " + p.icon} />
                  </div>
                  <div className="qt-platform-name">{p.name}</div>
                  <p className="qt-platform-steps">{p.steps}</p>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => this.copy("platform-" + p.id, this.htmlSnippet())}>
                    <i className={"mdi " + (this.state.copied === "platform-" + p.id ? "mdi-check" : "mdi-content-copy") + " me-1"} />
                    Copy snippet
                  </button>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    );
  }

  renderUsage() {
    const s = this.state;
    const u = s.usage;
    const funnelSteps = u
      ? [
          { key: "config", label: "Script loads", icon: "mdi-download", value: u.config_30d ?? 0 },
          { key: "open", label: "Opens", icon: "mdi-gesture-tap", value: u.opens_30d ?? 0 },
          { key: "quoted", label: "Quotes", icon: "mdi-calculator", value: u.quotes_30d ?? 0 },
          { key: "booked", label: "Bookings", icon: "mdi-check-circle", value: u.bookings_30d ?? 0 },
        ]
      : [];
    const funnelMax = Math.max(...funnelSteps.map((step) => step.value), 1);
    const quoteFromOpen = widgetFunnelPct(u?.quotes_30d, u?.opens_30d);
    const bookFromQuote = widgetFunnelPct(u?.bookings_30d, u?.quotes_30d);
    const hasActivity = funnelSteps.some((step) => step.value > 0);
    const trendDays = (u?.by_day || []).slice(-14);

    return (
      <div className="qt-card">
        <div className="qt-card-body">
          {this.sectionHead(
            "mdi-chart-line",
            "teal",
            "Widget activity",
            "How shoppers move from your embedded script to a confirmed delivery (last 30 days)."
          )}
          <div className="d-flex justify-content-end mb-3">
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={this.fetchUsage} disabled={s.usageLoading}>
              {s.usageLoading ? <Spinner size="sm" /> : <><i className="mdi mdi-refresh me-1" />Refresh</>}
            </button>
          </div>
          {s.usageLoading && !u ? (
            <div className="text-center py-4"><Spinner /></div>
          ) : u ? (
            <>
              <div className="qt-usage-journey mb-4">
                <div className="qt-usage-journey__copy">
                  <h6 className="small fw-bold mb-1">Widget funnel</h6>
                  <p className="text-muted small mb-0">
                    Script loads on your store → shopper opens quote → price calculated → booking confirmed on QikTruck.
                  </p>
                  {hasActivity ? (
                    <div className="qt-usage-conversions mt-2">
                      {quoteFromOpen != null ? (
                        <span className="qt-usage-conversion">
                          <i className="mdi mdi-arrow-right me-1" />
                          {quoteFromOpen}% of opens get a quote
                        </span>
                      ) : null}
                      {bookFromQuote != null ? (
                        <span className="qt-usage-conversion">
                          <i className="mdi mdi-arrow-right me-1" />
                          {bookFromQuote}% of quotes become bookings
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <WidgetEmbedMock stage="tracking" size="sm" />
              </div>

              <div className="qt-funnel mb-4">
                {funnelSteps.map((step, index) => {
                  const prev = index > 0 ? funnelSteps[index - 1].value : 0;
                  const stepPct = widgetFunnelPct(step.value, prev);
                  const barPct = Math.max(8, Math.round((step.value / funnelMax) * 100));
                  return (
                    <div key={step.key} className="qt-funnel-step">
                      <div className="qt-funnel-step__head">
                        <span className="qt-funnel-step__label">
                          <i className={"mdi " + step.icon + " me-1"} />
                          {step.label}
                        </span>
                        <span className="qt-funnel-step__value">{step.value}</span>
                      </div>
                      <div className="qt-funnel-step__bar" aria-hidden="true">
                        <span className={"qt-funnel-step__fill qt-funnel-step__fill--" + step.key} style={{ width: barPct + "%" }} />
                      </div>
                      {index > 0 && stepPct != null ? (
                        <div className="qt-funnel-step__rate">{stepPct}% of previous step</div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {trendDays.length > 0 ? (
                <div className="qt-usage-trend mb-4">
                  <h6 className="small fw-bold text-uppercase text-muted mb-2">
                    <i className="mdi mdi-chart-timeline-variant me-1" />Daily activity (last 14 days)
                  </h6>
                  <div className="qt-usage-trend__grid">
                    {trendDays.map((row, i) => {
                      const peak = Math.max(
                        ...trendDays.map((d) => Math.max(d.opens ?? 0, d.quotes ?? 0, d.bookings ?? 0)),
                        1
                      );
                      const dayLabel = row.day ? row.day.slice(5) : "";
                      return (
                        <div key={i} className="qt-usage-trend__day" title={row.day || ""}>
                          <div className="qt-usage-trend__bars">
                            <span
                              className="qt-usage-trend__bar qt-usage-trend__bar--open"
                              style={{ height: Math.max(4, Math.round(((row.opens ?? 0) / peak) * 100)) + "%" }}
                            />
                            <span
                              className="qt-usage-trend__bar qt-usage-trend__bar--quote"
                              style={{ height: Math.max(4, Math.round(((row.quotes ?? 0) / peak) * 100)) + "%" }}
                            />
                            <span
                              className="qt-usage-trend__bar qt-usage-trend__bar--book"
                              style={{ height: Math.max(4, Math.round(((row.bookings ?? 0) / peak) * 100)) + "%" }}
                            />
                          </div>
                          <span className="qt-usage-trend__label">{dayLabel}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="qt-usage-trend__legend">
                    <span><i className="qt-usage-trend__dot qt-usage-trend__dot--open" />Opens</span>
                    <span><i className="qt-usage-trend__dot qt-usage-trend__dot--quote" />Quotes</span>
                    <span><i className="qt-usage-trend__dot qt-usage-trend__dot--book" />Bookings</span>
                  </div>
                </div>
              ) : null}

              <Row className="g-3 mb-4">
                <Col sm={6} md={3}>
                  <div className="qt-metric">
                    <div className="qt-metric-value">{u.by_domain?.length || 0}</div>
                    <div className="qt-metric-label"><i className="mdi mdi-web me-1" />Active domains</div>
                  </div>
                </Col>
                <Col sm={6} md={3}>
                  <div className="qt-metric">
                    <div className="qt-metric-value">{u.funnel_30d ?? 0}</div>
                    <div className="qt-metric-label"><i className="mdi mdi-filter-variant me-1" />Funnel events</div>
                  </div>
                </Col>
                <Col sm={6} md={3}>
                  <div className="qt-metric">
                    <div className="qt-metric-value">{u.total_30d ?? 0}</div>
                    <div className="qt-metric-label"><i className="mdi mdi-counter me-1" />All events</div>
                  </div>
                </Col>
              </Row>

              <h6 className="small fw-bold text-uppercase text-muted mb-2">
                <i className="mdi mdi-web me-1" />By domain
              </h6>
              <p className="text-muted small mb-2">
                Quotes and bookings may show under the QikTruck checkout domain when shoppers complete payment in the widget iframe.
              </p>
              {(!u.by_domain || u.by_domain.length === 0) ? (
                <div className="qt-empty py-3 mb-3">
                  <i className="mdi mdi-web" />
                  <p className="small mb-0">Domain breakdown appears once shoppers use the widget on verified sites.</p>
                </div>
              ) : (
                <Table size="sm" className="mb-4 qt-usage-domain-table">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th className="text-end">Loads</th>
                      <th className="text-end">Opens</th>
                      <th className="text-end">Quotes</th>
                      <th className="text-end">Bookings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {u.by_domain.map((r, i) => (
                      <tr key={i}>
                        <td><i className="mdi mdi-web me-1 text-muted" />{r.domain || "(unknown)"}</td>
                        <td className="text-end text-muted">{r.config ?? 0}</td>
                        <td className="text-end">{r.opens ?? 0}</td>
                        <td className="text-end">{r.quotes ?? 0}</td>
                        <td className="text-end fw-semibold">{r.bookings ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <h6 className="small fw-bold text-uppercase text-muted mb-2">
                <i className="mdi mdi-chart-donut me-1" />Event breakdown
              </h6>
              {(!u.by_event || u.by_event.length === 0) ? (
                <div className="qt-empty py-3">
                  <i className="mdi mdi-chart-bar" />
                  <p className="small mb-0">No widget activity yet. Install the snippet and open the widget on your store.</p>
                </div>
              ) : (
                <div className="qt-usage-events">
                  {u.by_event.map((r, i) => (
                    <div key={i} className="qt-usage-event">
                      <span className="qt-usage-event__label">{widgetEventLabel(r.event)}</span>
                      <span className="qt-usage-event__count">{r.hits}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-muted">Loading usage data…</p>
          )}
        </div>
      </div>
    );
  }

  renderDocs() {
    return (
      <div className="qt-card">
        <div className="qt-card-body">
          {this.sectionHead(
            "mdi-book-open-variant",
            "orange",
            "Embed documentation",
            "Widget script, checkout URL parameters, and JavaScript API."
          )}

          <div className="mb-4">
            <p className="text-muted small mb-2">
              Hosted checkout covers quote through trip tracking - nothing heavy to host yourself.
            </p>
            <EmbedFlowStrip stages={WIDGET_EMBED_STAGES} size="xs" />
          </div>

          <div className="d-flex flex-wrap gap-2 mb-4">
            <a href="/api-docs?product=embed" className="btn btn-primary">
              <i className="mdi mdi-book-open-page-variant me-1" />
              Open embed documentation
            </a>
            <button type="button" className="btn btn-outline-primary" onClick={() => this.go("install")}>
              <i className="mdi mdi-code-tags me-1" />
              Install snippet
            </button>
          </div>

          <div className="qt-config-block">
            <div className="qt-config-block-head"><i className="mdi mdi-rocket" /><h6>Quick start</h6></div>
            <ol className="small mb-0 ps-3">
              <li className="mb-1">Verify your domain in the Domains tab</li>
              <li className="mb-1">Save warehouses in Configuration</li>
              <li className="mb-1">Copy the install snippet from the Install tab</li>
              <li>Shoppers checkout at <code>qiktruck.co.za/widget/quote</code></li>
            </ol>
          </div>

          <h6 className="small fw-bold mt-4 mb-2">Default embed</h6>
          {this.codeBlock(this.htmlSnippet(), "docs-embed")}

          <h6 className="small fw-bold mt-4 mb-2"><i className="mdi mdi-tune me-1" style={{ color: "#f17516" }} />Script attributes</h6>
          <Table size="sm" bordered responsive className="small">
            <thead className="table-light"><tr><th>Attribute</th><th>What it does</th><th>Default</th></tr></thead>
            <tbody>{CONFIG_OPTIONS.map((r, i) => (<tr key={i}><td><code>{r[0]}</code></td><td>{r[1]}</td><td><code>{r[2]}</code></td></tr>))}</tbody>
          </Table>

          <p className="small mt-3 mb-0">
            <a href="/api-docs?product=embed#widget-embed">Full widget &amp; checkout reference →</a>
          </p>
        </div>
      </div>
    );
  }

  renderSection() {
    switch (this.state.activeSection) {
      case "domains": return this.renderDomains();
      case "config": return this.renderConfig();
      case "install": return this.renderInstall();
      case "usage": return this.renderUsage();
      case "docs": return this.renderDocs();
      default: return this.renderOverview();
    }
  }

  renderIntegrationPicker() {
    const active = this.state.activeIntegration;
    return (
      <div className="qt-integration-grid mb-4">
        {INTEGRATION_TYPES.map((item) => {
          const isActive = active === item.id;
          const isSoon = item.status === "soon";
          return (
            <button
              key={item.id}
              type="button"
              className={
                "qt-integration-card" +
                (isActive ? " is-active" : "") +
                (isSoon ? " is-soon" : "")
              }
              onClick={() => this.pickIntegration(item.id)}
              disabled={isSoon}
            >
              <div className="qt-integration-card-top">
                <span className="qt-integration-card-icon">
                  <i className={"mdi " + item.icon} />
                </span>
                {isSoon ? (
                  <span className="qt-integration-soon">Soon</span>
                ) : isActive ? (
                  <span className="qt-integration-active-dot" aria-label="Selected" />
                ) : null}
              </div>
              <div className="qt-integration-card-label">{item.label}</div>
              <p className="qt-integration-card-desc">{item.desc}</p>
              <span className="qt-integration-card-eta">{item.eta}</span>
            </button>
          );
        })}
      </div>
    );
  }

  renderPartnerOverview() {
    const prog = this.partnerSetupProgress();
    const next = this.nextPartnerSetupStep();
    const submitted = prog.submitted;
    const keysUnlocked = !!this.state.partnerAccess?.hq_provisioned;
    return (
      <>
        {this.renderTrustStrip()}
        <Card className="mb-4 border-0 qt-hero qt-hero--split qt-hero--partner">
          <CardBody className="py-4">
            <Row className="align-items-center g-4">
              <Col lg={7}>
                <div className="qt-hero-eyebrow">
                  {submitted ? (
                    <span className="qt-live-badge"><span className="qt-live-dot" /> Application in review</span>
                  ) : (
                    <span>Partner API · Custom UX &amp; backends</span>
                  )}
                </div>
                <h3 className="mb-2">Quote, book, and track from your own software</h3>
                <p className="qt-hero-lead mb-3">
                  For teams building a custom shopper experience, headless storefront, ERP/TMS workflow, or mobile
                  app. QikTruck reviews your application, then your developer integrates via secure API keys. Want a
                  drop-in quote button instead? Use the Website Widget.
                </p>
                <div className="qt-hero-pills">
                  <span className="qt-hero-pill"><i className="mdi mdi-key-variant" /> Secure API keys</span>
                  <span className="qt-hero-pill"><i className="mdi mdi-server" /> Server-side integration</span>
                  <span className="qt-hero-pill"><i className="mdi mdi-shield-check" /> Review before go-live</span>
                </div>
                {!submitted && (
                  <button type="button" className="btn btn-primary mt-3 me-2" onClick={() => this.go("apply")}>
                    <i className="mdi mdi-key-variant me-1" />
                    Request API access - step 1
                  </button>
                )}
                <a href={DOCS_PATH} className={"btn mt-3 " + (submitted ? "btn-primary" : "btn-outline-primary")}>
                  <i className="mdi mdi-book-open-page-variant me-1" />
                  Full API docs
                </a>
              </Col>
              <Col lg={5} className="d-flex justify-content-lg-end justify-content-center">
                {this.renderPartnerApiMock()}
              </Col>
            </Row>
          </CardBody>
        </Card>

        <div className="qt-card mb-4">
          <div className="qt-card-body">
            <h5 className="mb-1 qt-card-heading">
              <i className="mdi mdi-api me-2" />
              API lifecycle
            </h5>
            <p className="text-muted small mb-3">
              Your app calls catalog, quote, book, payment, and status endpoints - custom checkout UX or
              back-office automation.
            </p>
            <PartnerApiFlowStrip stages={PARTNER_API_STAGES} />
          </div>
        </div>

        <div className="qt-flow-row mb-4">
          {[
            { n: 1, i: "mdi-file-document-edit", t: "Apply", d: "Company details, domains, and terms - we verify your business." },
            { n: 2, i: "mdi-email-fast", t: "Create keys", d: "After approval, log in and create sandbox or production keys yourself. They are only shown once." },
            { n: 3, i: "mdi-code-braces", t: "Integrate", d: "Your developer connects: get price → book → track until delivered." },
          ].map((x, idx) => (
            <React.Fragment key={x.n}>
              <div className="qt-flow-card">
                <div className={"qt-flow-icon qt-flow-icon--" + x.n}>
                  <i className={"mdi " + x.i} />
                </div>
                <div className="qt-flow-step">Step {x.n}</div>
                <div className="qt-flow-title">{x.t}</div>
                <p className="qt-flow-desc">{x.d}</p>
              </div>
              {idx < 2 && <div className="qt-flow-connector" aria-hidden="true"><i className="mdi mdi-chevron-right" /></div>}
            </React.Fragment>
          ))}
        </div>

        <div className="qt-card mb-4">
          <div className="qt-card-body">
            <h5 className="mb-1 qt-card-heading">
              <i className="mdi mdi-format-list-checks me-2" />
              Partner API checklist
            </h5>
            <p className="text-muted small mb-3">
              {submitted
                ? "Application received - we'll follow up by email. Explore the API while you wait."
                : "Start with your access application. Most teams complete the form in ~10 minutes."}
            </p>
            {[
              {
                done: true,
                icon: "mdi-flash",
                t: "Understand Partner API vs widget",
                h: "Partner API = full REST control (custom web shops, apps, ERP/TMS). Widget = fastest no-code embed.",
                s: "overview",
                a: "You're here",
                btn: "btn-outline-secondary",
                disabled: true,
              },
              {
                done: submitted,
                icon: "mdi-key-variant",
                t: "Submit access application",
                h: "Legal name, registered address, domains, social profiles, and terms declarations.",
                s: "apply",
                a: submitted ? "Submitted" : "Start application",
                btn: submitted ? "btn-success" : "btn-primary",
              },
              {
                done: keysUnlocked,
                icon: "mdi-shield-check",
                t: "Access activated",
                h: "We'll email you when QikTruck approves. Then create sandbox keys in the API keys tab.",
                s: "keys",
                a: keysUnlocked ? "Create keys" : "What to expect",
                btn: keysUnlocked ? "btn-primary" : "btn-outline-primary",
              },
              {
                done: false,
                icon: "mdi-book-open-page-variant",
                t: "Read API reference",
                h: "Plain-language guides plus full technical reference for your developer.",
                s: "docs",
                a: "View reference",
                btn: "btn-outline-primary",
              },
            ].map((step, i) => (
              <div key={i} className={"qt-setup-step" + (step.done ? " is-done" : "")}>
                <div className="qt-setup-num">
                  {step.done ? <i className="mdi mdi-check" /> : i + 1}
                </div>
                <div className="qt-setup-body">
                  <div className="qt-setup-title">
                    <i className={"mdi " + step.icon + " me-1"} />
                    {step.t}
                  </div>
                  <p className="qt-setup-hint">{step.h}</p>
                </div>
                <div className="qt-setup-action">
                  <button
                    type="button"
                    className={"btn btn-sm " + step.btn}
                    disabled={step.disabled}
                    onClick={() => this.go(step.s)}
                  >
                    {step.a}
                    {!step.disabled && <i className="mdi mdi-arrow-right ms-1" />}
                  </button>
                </div>
              </div>
            ))}
            {!submitted && (
              <div className="text-center pt-3 mt-2 border-top">
                <button type="button" className="btn btn-primary btn-lg" onClick={() => this.go("apply")}>
                  <i className={"mdi " + next.icon + " me-2"} />
                  {next.label}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="qt-tip qt-tip--info">
          <i className="mdi mdi-information-outline" />
          <div>
            <strong>Choosing an approach:</strong>{" "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => this.pickIntegration("widget")}>
              Website Widget
            </button>{" "}
            is fastest for a standard product-page quote (one script tag). Partner API fits when developers own the
            shopper UX - custom website shops, mobile apps, or ERP/TMS automation.
          </div>
        </div>
      </>
    );
  }

  renderPartnerApply() {
    const s = this.state;
    return (
      <div className="qt-card qt-card--partner-form">
        <div className="qt-card-body">
          {this.sectionHead(
            "mdi-key-variant",
            "orange",
            "Request API access",
            "Step 1 of onboarding - complete company details, registered address, domains, and terms. Access is enabled after QikTruck review; you create keys here once active."
          )}
          <PartnerAccessPanel
            sourceRef="customer-hq:integrations:enterprise"
            knownDomains={s.domains || []}
            requireLogin
            onSuccess={this.handlePartnerAccessSuccess}
            onAccessLoaded={(access) => {
              const appStatus = access?.application_status;
              if (appStatus && appStatus.state !== "none") {
                this.setState({
                  partnerAccess: access,
                  partnerAccessSubmitted: true,
                  partnerAccessEmail:
                    appStatus.latest_application?.email || this.state.partnerAccessEmail,
                });
              } else if (access) {
                this.setState({ partnerAccess: access });
              }
            }}
          />
        </div>
      </div>
    );
  }

  renderPartnerDocs() {
    const liveBase = API_BASE;
    return (
      <>
        <div className="qt-card mb-4">
          <div className="qt-card-body">
            {this.sectionHead(
              "mdi-transit-connection-variant",
              "mint",
              "Request flow",
              "Typical server-side sequence: vehicle catalog, quote, book, pay, track."
            )}
            <PartnerApiFlowStrip stages={PARTNER_API_STAGES} size="xs" />
          </div>
        </div>

        <div className="qt-card mb-4">
          <div className="qt-card-body">
            {this.sectionHead(
              "mdi-tape-measure",
              "teal",
              "Measurements",
              "Prices, distances, and durations use fixed units across all responses."
            )}
            <Table size="sm" bordered responsive className="small mb-0">
              <thead className="table-light">
                <tr><th>Field</th><th>Unit</th><th>Example</th></tr>
              </thead>
              <tbody>
                {MEASUREMENTS.map((m) => (
                  <tr key={m.title}>
                    <td><strong>{m.title}</strong></td>
                    <td>{m.unit}</td>
                    <td><code>{m.example}</code></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>

        <div className="qt-card mb-4">
          <div className="qt-card-body">
            {this.sectionHead(
              "mdi-api",
              "mint",
              "Quick reference",
              "Summary for your developer. Full docs include plain-language guides and technical detail."
            )}
            <Table size="sm" bordered responsive className="small">
              <thead className="table-light">
                <tr><th>Area</th><th>Endpoints</th></tr>
              </thead>
              <tbody>
                {API_CATALOG.map((row) => (
                  <tr key={row.area}>
                    <td><strong>{row.area}</strong></td>
                    <td><code>{row.routes}</code></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="small text-muted mt-3 mb-2">
              Base URL: <code>{liveBase}/v1/partner</code> · Auth:{" "}
              <code>Authorization: Bearer qt_live_…</code>
            </p>
            <Alert color="info" className="small mb-3">
              <strong>trucktype &amp; category:</strong> call{" "}
              <code>GET /v1/partner/vehicle-types</code> first. Use <code>vehicles[].type</code> as{" "}
              <code>trucktype</code> (where <code>sell: true</code>) and pick <code>category</code> from that
              vehicle&apos;s <code>categories</code> array.{" "}
              <a href={`${DOCS_PATH}#vehicle-types`}>Vehicle catalog</a> ·{" "}
              <a href={`${DOCS_PATH}#quoting`}>Create quote</a>
            </Alert>
            <a href={DOCS_PATH} className="btn btn-primary me-2">
              <i className="mdi mdi-book-open-page-variant me-1" />
              Open full Partner API docs
            </a>
            <a href={`${DOCS_PATH}#field-reference`} className="btn btn-outline-primary">
              Field reference
            </a>
          </div>
        </div>
      </>
    );
  }

  renderPartnerKeys() {
    const s = this.state;
    const access = s.partnerAccess || {};
    const keys = s.partnerKeys || [];
    const activeKeys = keys.filter((k) => k.status === "active");

    if (s.partnerKeysLoading && !keys.length) {
      return (
        <div className="qt-card">
          <div className="qt-card-body text-center py-5">
            <Spinner color="primary" /> Loading API keys…
          </div>
        </div>
      );
    }

    if (access.partner_api_suspended) {
      return (
        <div className="qt-card">
          <div className="qt-card-body">
            <Alert color="danger" className="mb-0">
              <strong>Partner API access suspended.</strong> Contact{" "}
              <a href="mailto:support@qiktruck.co.za">support@qiktruck.co.za</a> to restore access.
            </Alert>
          </div>
        </div>
      );
    }

    if (!access.hq_provisioned) {
      return (
        <div className="qt-card">
          <div className="qt-card-body">
            {this.sectionHead(
              "mdi-key",
              "orange",
              "API keys",
              "Self-service key management unlocks after QikTruck activates your Partner API access."
            )}
            <Alert color="info" className="mb-3">
              <strong>Waiting for activation.</strong> Submit your{" "}
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => this.go("apply")}>
                access application
              </button>{" "}
              if you haven&apos;t already. QikTruck reviews every Partner API account and emails you when access is
              active. Then create and manage keys here in the business portal.
            </Alert>
            <p className="small text-muted mb-0">
              Keys are never shown again after creation. Store them in your secrets manager immediately.
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        {s.partnerIssuedKey ? (
          <Alert color="warning" className="mb-3">
            <strong>Copy your new API key now.</strong> We cannot display it again.
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
              <code className="small flex-grow-1 text-break">{s.partnerIssuedKey}</code>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark"
                onClick={() => this.copyPartnerKey(s.partnerIssuedKey)}
              >
                <i className="mdi mdi-content-copy me-1" />
                {s.copied === "key" ? "Copied" : "Copy"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => this.setState({ partnerIssuedKey: null })}
              >
                Dismiss
              </button>
            </div>
          </Alert>
        ) : null}

        <div className="qt-card mb-4">
          <div className="qt-card-body">
            {this.sectionHead(
              "mdi-key",
              "orange",
              "Your API keys",
              "Create and revoke keys for your account. QikTruck retains oversight and can suspend access at any time."
            )}

            {activeKeys.length === 0 ? (
              <Alert color="info" className="mb-3">
                No active keys. Create a sandbox key to start testing, or contact support if your issued key was revoked.
              </Alert>
            ) : null}

            <Table size="sm" bordered responsive className="small mb-4">
              <thead className="table-light">
                <tr>
                  <th>Label</th>
                  <th>Key</th>
                  <th>Environment</th>
                  <th>Created</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted text-center py-3">
                      No keys yet
                    </td>
                  </tr>
                ) : (
                  keys.map((k) => (
                    <tr key={k.id}>
                      <td>{k.label || "-"}</td>
                      <td><code>{k.masked_key}</code></td>
                      <td>{k.key_prefix === "qt_live" ? "Production" : "Sandbox"}</td>
                      <td>{k.created_at ? new Date(k.created_at).toLocaleDateString() : "-"}</td>
                      <td>{k.created_by_type === "merchant" ? "You" : "QikTruck"}</td>
                      <td>
                        <span className={"badge bg-" + (k.status === "active" ? "success" : "secondary")}>
                          {k.status}
                        </span>
                      </td>
                      <td>
                        {k.status === "active" ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={s.partnerKeyRevoking === k.id}
                            onClick={() => this.revokePartnerKey(k.id)}
                          >
                            {s.partnerKeyRevoking === k.id ? "Revoking…" : "Revoke"}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {access.self_service_enabled ? (
              <div className="border-top pt-3">
                <h6 className="mb-3">Create new key</h6>
                {access.partner_api_live_enabled ? (
                  <Alert color="success" className="py-2 small mb-3">
                    <i className="mdi mdi-check-circle me-1" />
                    Production access enabled - you can create sandbox and live keys.
                  </Alert>
                ) : (
                  <Alert color="info" className="py-2 small mb-3">
                    <i className="mdi mdi-information-outline me-1" />
                    Sandbox only until QikTruck enables go-live for production <code>qt_live_</code> keys.
                  </Alert>
                )}
                <Row className="g-2 align-items-end">
                  <Col md={5}>
                    <label className="form-label small text-muted">Label</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. Staging server"
                      value={s.partnerNewKeyLabel}
                      onChange={(e) => this.setState({ partnerNewKeyLabel: e.target.value })}
                    />
                  </Col>
                  <Col md={4}>
                    <label className="form-label small text-muted">Environment</label>
                    <select
                      className="form-select form-select-sm"
                      value={s.partnerNewKeyPrefix}
                      onChange={(e) => this.setState({ partnerNewKeyPrefix: e.target.value })}
                    >
                      <option value="qt_test">Sandbox (qt_test_)</option>
                      {access.partner_api_live_enabled ? (
                        <option value="qt_live">Production (qt_live_)</option>
                      ) : null}
                    </select>
                  </Col>
                  <Col md={3}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm w-100"
                      disabled={s.partnerKeyCreating}
                      onClick={this.createPartnerKey}
                    >
                      {s.partnerKeyCreating ? "Creating…" : "Create key"}
                    </button>
                  </Col>
                </Row>
                <p className="small text-muted mt-2 mb-0">
                  New keys are shown once - store them server-side only. QikTruck can suspend access at any time.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </>
    );
  }

  renderPartnerSection() {
    switch (this.state.activeSection) {
      case "plans":
        return this.renderPartnerPlans();
      case "apply":
        return this.renderPartnerApply();
      case "keys":
        return this.renderPartnerKeys();
      case "usage":
        return this.renderPartnerUsage();
      case "billing":
        return this.renderPartnerBilling();
      case "claims":
        return this.renderPartnerClaims();
      case "docs":
        return this.renderPartnerDocs();
      default:
        return this.renderPartnerOverview();
    }
  }

  renderPartnerConsole() {
    const s = this.state;
    return (
      <>
        {this.renderPartnerStatusBar()}
        <div className="qt-mobile-nav d-lg-none">
          {PARTNER_SECTIONS.map((sec) => (
            <button
              key={sec.id}
              type="button"
              className={"qt-mobile-nav-btn" + (s.activeSection === sec.id ? " is-active" : "")}
              onClick={() => this.go(sec.id)}
            >
              <i className={"mdi " + sec.icon} />
              {sec.label}
            </button>
          ))}
        </div>
        <Row>
          <Col lg={3} className="mb-3 d-none d-lg-block">
            <div className="qt-sidebar">
              <div className="qt-sidebar-card">
                <div className="qt-sidebar-head">
                  <i className="mdi mdi-flash me-1" /> Partner API
                </div>
                <Nav vertical className="qt-side-nav">
                  {PARTNER_SECTIONS.map((sec) => (
                    <NavItem key={sec.id}>
                      <NavLink
                        className={classnames({ active: s.activeSection === sec.id })}
                        onClick={() => this.go(sec.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <i className={"mdi " + sec.icon} />
                        {sec.label}
                        {sec.id === "apply" && !s.partnerAccessSubmitted && (
                          <span className="qt-nav-badge">Start here</span>
                        )}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
                {this.renderPartnerSidebarProgress()}
              </div>
            </div>
          </Col>
          <Col lg={9}>
            <div className="qt-section-panel" key={s.activeSection}>
              {this.renderPartnerSection()}
            </div>
          </Col>
        </Row>
      </>
    );
  }

  renderComingSoon(name, blurb) {
    return (
      <>
        <div className="qt-card qt-card--waitlist">
          <div className="qt-card-body">
            <div className="qt-waitlist-icon"><i className="mdi mdi-rocket" /></div>
            <h4 className="mb-2">{name}</h4>
            <p className="text-muted mb-3">{blurb}</p>
            <p className="small text-muted mb-3">
              We are building native {name} integration so you won&apos;t need to paste code manually.
            </p>
            <a href={"mailto:support@qiktruck.co.za?subject=" + encodeURIComponent(name + " integration - early access")} className="btn btn-primary">
              <i className="mdi mdi-email-outline me-1" />
              Join waitlist
            </a>
            <button type="button" className="btn btn-outline-primary ms-2" onClick={() => this.pickIntegration("widget")}>
              Use website widget now
            </button>
          </div>
        </div>
      </>
    );
  }

  renderWooCommerce() {
    return (
      <>
        <div className="qt-card mb-3">
          <div className="qt-card-body">
            <h4 className="mb-2">WooCommerce plugin</h4>
            <p className="text-muted mb-3">
              Show live QikTruck delivery quotes at checkout and book trips automatically when orders are paid.
              Requires a Partner API key from this dashboard.
            </p>
            <a
              href="mailto:support@qiktruck.co.za?subject=WooCommerce%20plugin%20download"
              className="btn btn-primary me-2"
            >
              <i className="mdi mdi-download me-1" />
              Request plugin ZIP
            </a>
            <button type="button" className="btn btn-outline-primary" onClick={() => this.pickIntegration("partner")}>
              Get Partner API key
            </button>
          </div>
        </div>
        <div className="qt-card">
          <div className="qt-card-body">
            <h5 className="mb-3">Setup (5 minutes)</h5>
            <ol className="mb-0 ps-3">
              <li className="mb-2">Install and activate <strong>QikTruck Delivery for WooCommerce</strong> on your WordPress site (WooCommerce 7+).</li>
              <li className="mb-2">Go to <strong>WooCommerce → Settings → Shipping</strong>, open your zone, and add <strong>QikTruck Delivery</strong>.</li>
              <li className="mb-2">Paste your <code>qt_test_*</code> sandbox key, set your warehouse pickup address, and click <strong>Test connection</strong>.</li>
              <li className="mb-2">Add a Google Maps Geocoding API key for reliable address lookup in production.</li>
              <li className="mb-2">Place a test order with billing email and phone - the shipping rate appears once the delivery address is complete.</li>
              <li>When ready, swap to your <code>qt_live_*</code> key. Delivery is billed to your QikTruck merchant account on booking.</li>
            </ol>
          </div>
        </div>
      </>
    );
  }

  renderShopify() {
    return (
      <>
        <div className="qt-card mb-3">
          <div className="qt-card-body">
            <h4 className="mb-2">Shopify app</h4>
            <p className="text-muted mb-3">
              Show live QikTruck delivery rates at Shopify checkout and book trips
              automatically when orders are paid. Requires a Partner API key from this
              dashboard.
            </p>
            <a
              href="https://shopify-app.qiktruck.co.za"
              className="btn btn-primary me-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="mdi mdi-open-in-new me-1" />
              Open QikTruck Shopify app
            </a>
            <button type="button" className="btn btn-outline-primary" onClick={() => this.pickIntegration("partner")}>
              Get Partner API key
            </button>
          </div>
        </div>
        <div className="qt-card">
          <div className="qt-card-body">
            <h5 className="mb-3">Setup (5 minutes)</h5>
            <ol className="mb-0 ps-3">
              <li className="mb-2">
                Install <strong>QikTruck On-Demand Trips</strong> on your Shopify store
                (development stores work for testing).
              </li>
              <li className="mb-2">
                In the app, open <strong>Settings</strong> and paste your{" "}
                <code>qt_test_*</code> sandbox Partner API key.
              </li>
              <li className="mb-2">
                Set your warehouse pickup address, default vehicle, and optional Google
                Maps Geocoding API key, then click <strong>Save settings</strong>.
              </li>
              <li className="mb-2">
                At checkout, customers enter a delivery address and select{" "}
                <strong>QikTruck Delivery</strong> when rates appear.
              </li>
              <li className="mb-2">
                Place a test order with billing email and phone. When payment completes,
                the trip is booked automatically and the QikTruck trip ID is added to
                the order note.
              </li>
              <li>
                When ready, swap to your <code>qt_live_*</code> key. Delivery is billed
                to your QikTruck merchant account on booking. Shopify Advanced (or a
                dev store) is required for third-party carrier rates.
              </li>
            </ol>
          </div>
        </div>
      </>
    );
  }

  renderIntegrationBody() {
    const type = this.state.activeIntegration;
    if (type === "woocommerce") {
      return this.renderWooCommerce();
    }
    if (type === "shopify") {
      return this.renderShopify();
    }
    if (type === "magento") {
      return this.renderComingSoon(
        "Magento",
        "Magento extension for live delivery quotes and fulfilment handoff to QikTruck."
      );
    }
    return null;
  }

  renderWidgetConsole() {
    const s = this.state;
    return (
      <>
        {this.renderWidgetStatusBar()}
        <div className="qt-mobile-nav d-lg-none">
          {SECTIONS.map((sec) => (
            <button
              key={sec.id}
              type="button"
              className={"qt-mobile-nav-btn" + (s.activeSection === sec.id ? " is-active" : "")}
              onClick={() => this.go(sec.id)}
            >
              <i className={"mdi " + sec.icon} />
              {sec.label}
            </button>
          ))}
        </div>
        <Row>
          <Col lg={3} className="mb-3 d-none d-lg-block">
            <div className="qt-sidebar">
              <div className="qt-sidebar-card">
                <div className="qt-sidebar-head">
                  <i className="mdi mdi-puzzle me-1" /> Website widget
                </div>
                <Nav vertical className="qt-side-nav">
                  {SECTIONS.map((sec) => (
                    <NavItem key={sec.id}>
                      <NavLink
                        className={classnames({ active: s.activeSection === sec.id })}
                        onClick={() => this.go(sec.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <i className={"mdi " + sec.icon} />
                        {sec.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
                {this.renderSidebarProgress()}
              </div>
            </div>
          </Col>
          <Col lg={9}>
            <div className="qt-section-panel" key={s.activeSection}>
              {this.renderSection()}
            </div>
          </Col>
        </Row>
      </>
    );
  }

  render() {
    const s = this.state;
    const isPartner = s.activeIntegration === "partner";
    const seoPath = isPartner ? "/integrations?type=partner" : "/integrations";
    return (
      <React.Fragment>
        <SeoHead
          title={
            isPartner
              ? "Partner API integrations | QikTruck Business"
              : "Website widget & integrations | QikTruck Business"
          }
          description={
            isPartner
              ? "Connect custom storefronts, ERP, TMS, or apps to QikTruck. Request API access, manage keys, and monitor Partner API usage."
              : "Add delivery quotes to WooCommerce, Shopify, or any website. Register domains, copy embed code, and track widget usage."
          }
          pathname={seoPath}
        />
        <ConfirmModal
          isOpen={!!s.confirmModal}
          title={s.confirmModal?.title}
          message={s.confirmModal?.message}
          confirmLabel={s.confirmModal?.confirmLabel}
          confirmColor={s.confirmModal?.confirmColor || "danger"}
          confirming={
            (s.confirmModal?.type === "revokePartnerKey" &&
              s.partnerKeyRevoking === s.confirmModal?.keyId) ||
            (s.confirmModal?.type === "removeDomain" && s.removing === s.confirmModal?.domain)
          }
          onCancel={this.closeConfirmModal}
          onConfirm={this.confirmModalAction}
        />
        <section className="qt-integrations">
          <Container>
            <div className="qt-page-head">
              <div className="qt-breadcrumb">
                <a href="/dashboard"><i className="mdi mdi-view-dashboard me-1" />Dashboard</a>
                {" / Developers / Integrations"}
              </div>
              <div className="qt-page-title-row">
                <div>
                  <h2 className="qt-page-title">
                    <i className="mdi mdi-link-variant" />
                    Integrations
                  </h2>
                  <p className="qt-page-lead">
                    Connect QikTruck to your store and systems - website widget, Partner API for external
                    developers, WooCommerce, Shopify, Magento and more.
                  </p>
                </div>
                {s.activeIntegration === "widget" && s.siteId && (
                  <div className="qt-site-chip">
                    <i className="mdi mdi-identifier" />
                    Site ID <code>{s.siteId}</code>
                  </div>
                )}
              </div>
            </div>
            {this.renderIntegrationPicker()}
            {s.error && <Alert color="danger"><i className="mdi mdi-alert-circle me-1" />{s.error}</Alert>}
            {s.notice && <Alert color="success"><i className="mdi mdi-check-circle me-1" />{s.notice}</Alert>}
            {s.loading && s.activeIntegration === "widget" ? (
              this.renderLoadingSkeleton()
            ) : s.activeIntegration === "widget" ? (
              this.renderWidgetConsole()
            ) : s.activeIntegration === "partner" ? (
              this.renderPartnerConsole()
            ) : (
              this.renderIntegrationBody()
            )}
          </Container>
        </section>
      </React.Fragment>
    );
  }
}

export default Integrations;
